/*
  Holster Finder Quiz
  Cold-traffic lander. Make > Model > Light > Carry > Priority > Match.

  The flow, copy and ranking come from the approved canvas prototype
  (handoff/holster-quiz/prototype-Main.dc.html). Two rules here differ from it
  DELIBERATELY, on product facts from Pablo 2026-09-28, and both are marked
  PRODUCT FACT below.

  This file must not depend on Boost or Algolia. The quiz links straight to
  product pages, so a search migration cannot break it. That is the whole
  reason it does not reuse the Holster Finder's ENGINE adapter.
*/
(function () {
  "use strict";

  /*
    BUILD STAMP. Bump this whenever a change must be confirmed as live.

    WHY A STAMP AND NOT A BYTE COUNT. Shopify MINIFIES theme .js and .css on
    serve, so the served file is legitimately much smaller than the file on the
    theme and a size comparison proves nothing. Measured 2026-09-28: 50,946
    bytes and 1,166 lines here, 28,655 bytes and 2 lines served, same build.
    The CSS goes 15,630 to 9,833, 587 lines to 2.

    That difference was briefly read as the CDN serving a stale asset. It was
    not. Every current string was present in the served file the whole time.
    Compare a STAMP, never a size.

    Read it out of the served file with:

      curl -sS "<asset url>" | grep -o 'BUILD="[0-9a-z.-]*"'

    NOT by grepping the comment next to it: minification strips comments, so
    only the assignment survives. Grepping this file locally for the same
    pattern also matches the example above, which is its own false positive.

    Or in the console on the page itself, which is the easier check:

      RGHolsterQuiz.build
  */
  var BUILD = "2026-10-31.89";

  var DATA_SELECTOR = "[data-rg-hq-data]";
  var PRODUCT_BASE = "/products/";

  /* ---------------------------------------------------------------- config */

  var IWB = [
    "IWB KYDEX Holster", "IWB KYDEX Plus Line Holster", "Tuckable IWB KYDEX Holster",
    "Sidecar Tuckable IWB KYDEX Holster", "Druid KYDEX Holster",
    "Athletic Wear Tuckable IWB Holster", "Trigger Guard Tuckable IWB KYDEX Holster",
    /*
      The three universal LIGHT-BEARING styles. They are in the IWB pool on
      purpose, unlike the plain universals: they are molded around the LIGHT,
      so they are a real answer for anyone who named one, including on a gun
      we do mold.
      They can never appear for a gun with nothing mounted, because
      available() only ORs universals in when a light is chosen.
    */
    "X-FER Weapon Mounted Light Holster",
    "LUX Universal Light Bearing Holster",
    "LUX V2 EXT Universal Light Bearing Holster"
  ];
  /*
    PRODUCT FACT, Pablo 2026-09-28: the Druid ships with both an IWB clip and
    an OWB paddle, so it belongs in BOTH pools. The prototype had it in IWB
    only, which hid it from everyone who chose outside the waistband. Its own
    blurb says "wear it inside or outside", so the two now agree.
  */
  var OWB = ["OWB Paddle KYDEX Holster", "OWB Belt Loop KYDEX Holster", "Druid KYDEX Holster"];

  /*
    Ties break on this order, which reflects the conversion data: standard IWB
    converts best, so it wins a tie against everything else.
  */
  /*
    CARRY POSITIONS, FROM custom.carry_position ON THE PRODUCTS.

    Read off the live catalogue on 2026-09-28 rather than invented. The
    metafield is a list.single_line_text_field and its values are STYLE-LEVEL:
    every product of a style carries the same list, so this is a style table
    and needs no per-product data in the page and no regeneration of the
    snippet.

    Verified per style against the Admin API. Two things the data settled:

    - OWB is Hip (3 o'clock) and nothing else, for both the Paddle and the
      Belt Loop. The single outside tile is the data, not a simplification.
    - No standard IWB style offers a strong-side hip: not IWB, Tuckable or
      Plus Line. The quiz was offering "Hip, Inside The Waistband at 3 to 4"
      for those, which the catalogue does not support. Athletic Wear DOES list
      Hip and Kidney, so hip is still reachable inside, through the product
      that actually claims it.

    A few products have the metafield EMPTY (two of four Trigger Guards, two
    of four Athletic Wear). Keying on style rather than product is what keeps
    those from silently losing their positions.

    Purse and Pocket have no clock position, so they carry no diagram.
  */
  /*
    WHAT MAKES THIS ONE DIFFERENT, in four or five words. Pablo 2026-09-28:
    the results should read good / better / best, and a shopper cannot see the
    difference between four KYDEX holsters from the photographs.

    Each label names the ONE thing that distinguishes this style from the
    others in the same list. Not a feature list and not a blurb: the blurb
    already explains the pick, this separates it from its neighbours.
  */
  var TAG = {
    "IWB KYDEX Holster":                        "Our Most Popular Holster",
    "IWB KYDEX Plus Line Holster":              "Claw Included For Max Concealment",
    "Tuckable IWB KYDEX Holster":               "Tuckable",
    "Trigger Guard Tuckable IWB KYDEX Holster": "Smallest Footprint",
    "Sidecar Tuckable IWB KYDEX Holster":       "Holds A Spare Mag",
    "Athletic Wear Tuckable IWB Holster":       "Beltless Carry",
    "Druid KYDEX Holster":                      "Most Rugged & Versatile",
    "OWB Paddle KYDEX Holster":                 "Quick On And Off",
    "OWB Belt Loop KYDEX Holster":              "Rides Tight To The Belt",
    /* These three mold around the LIGHT rather than the gun, which is the one
       thing a shopper has to understand before the result makes sense. "Fits
       By Your Light" read as a sorting instruction; naming both halves says
       what the holster actually does. Pablo 2026-09-30. */
    "X-FER Weapon Mounted Light Holster":       "Fits Your Light on Your Gun",
    "LUX Universal Light Bearing Holster":      "Fits Your Light on Your Gun",
    "LUX V2 EXT Universal Light Bearing Holster": "Fits Your Light on Your Gun",
    "Universal IWB Holster":                    "Fits 200+ Guns",
    "IWB Leather Holster":                      "Leather, Fits 200+ Guns"
  };

  var POSITIONS = {
    /*
      KIDNEY ADDED 2026-09-28, following Pablo's bulk edit to the IWB KYDEX
      Holster product type. It is behind the hip at 4 to 5, which does work
      inside the waistband where a strong-side hip does not.

      THE TABLE FOLLOWS THE DATA. Updated 2026-10-01 after Pablo's metafield
      edits for the carry guide: Plus Line now matches IWB KYDEX (adds Kidney),
      and Tuckable and Trigger Guard list Hip and Kidney as well. Every
      published product of each style was checked against the Admin API the
      same day. Druid, X-FER and LUX are unchanged; adding a position
      speculatively would offer one the product pages do not claim.
    */
    "IWB KYDEX Holster":                        ["appendix", "crossdraw", "kidney", "sob"],
    "IWB KYDEX Plus Line Holster":              ["appendix", "crossdraw", "kidney", "sob"],
    "Tuckable IWB KYDEX Holster":               ["appendix", "crossdraw", "hip", "kidney", "sob"],
    /*
      THE TRIGGER GUARD IS IWB. Pablo 2026-09-28. The metafield also lists
      Purse/Bag Carry and Pocket Carry; pocket support is GONE for now because
      it needs an accessory, and the purse case is served by the actual purse
      products rather than by putting a trigger guard holster in a bag. Both
      are metafield values to correct.
    */
    "Trigger Guard Tuckable IWB KYDEX Holster": ["appendix", "crossdraw", "hip", "kidney", "sob"],
    "Sidecar Tuckable IWB KYDEX Holster":       ["appendix"],
    "Athletic Wear Tuckable IWB Holster":       ["appendix", "crossdraw", "hip", "kidney", "sob"],
    "Druid KYDEX Holster":                      ["appendix", "crossdraw", "hip", "sob"],
    "OWB Paddle KYDEX Holster":                 ["hip"],
    "OWB Belt Loop KYDEX Holster":              ["hip"],
    "X-FER Weapon Mounted Light Holster":       ["appendix", "crossdraw", "sob"],
    "LUX Universal Light Bearing Holster":      ["appendix", "hip", "sob"],
    "LUX V2 EXT Universal Light Bearing Holster": ["appendix", "hip", "sob"]
  };

  /*
    THE DRUID IS THE ONE DUAL-MODE STYLE, and the metafield cannot express
    that. It lists appendix, cross draw, hip and small of back, which are its
    INSIDE positions plus the hip it rides at on the paddle. Left alone, a
    shopper who chose Outside was offered Small Of Back on the strength of a
    holster that only does that inside.

    So a style may declare what it offers when worn outside. Anything not
    listed here contributes its full list on both sides, which is correct for
    every single-mode style.
  */
  var POSITIONS_OWB = {
    "Druid KYDEX Holster": ["hip"]
  };

  /*
    Hours are for a RIGHT hand draw and mirror to (12 - h) for a left, which is
    the same mirror the diagrams use. Appendix is 1 o'clock, so it reads 11 for
    a left hand draw.

    NOTE the metafield labels these "Appendix Carry (1-2 O'Clock)". Pablo's
    call 2026-09-28 is 1 o'clock, so the wording here is ours and the metafield
    decides only WHICH positions exist, not how they are described.
  */
  var POS = {
    appendix:  { label: "Appendix", hour: 1,    order: 0 },
    crossdraw: { label: "Cross Draw", hour: 10.5, order: 1 },
    /*
      HIP IS AN OUTSIDE POSITION ONLY. Pablo 2026-09-28: "hip carry (3 O'clock)
      for IWB doesn't really work due to how we are shaped...it works for OWB
      though."

      The catalogue disagrees, and the catalogue is wrong: Athletic Wear lists
      Hip Carry (3 O'Clock) among its positions even though it is an inside
      holster. That is a data entry to fix, recorded in the audit, and until it
      is fixed this flag keeps the quiz from offering a position the body does
      not allow. It is a rule about anatomy, not about any one product, so it
      belongs here rather than in a per-style exception.
    */
    /*
      HIP READS AS A RANGE AND DRAWS AS A POINT. Pablo 2026-09-28: outside
      carry is hip at 2 to 4 o'clock, with the marker at 3. So `range` is what
      the shopper reads and `hour` is where the dot goes; they are different
      answers to different questions and neither can be derived from the other.
    */
    /*
      HIP IS VALID INSIDE, AND THE OVERRIDE THAT SAID OTHERWISE IS GONE.

      It carried outsideOnly, from "hip carry for IWB doesn't really work due
      to how we are shaped" on 2026-09-28. Pablo's own fitment matrix later
      that day contradicts it: Kydex IWB at strong-side hip is GOOD, set 0 to
      +5 for a vertical draw. Athletic Wear had listed hip all along and was
      treated as the data being wrong; it was not.

      THE SHAPE OF THE MISTAKE IS THE LESSON. A hardcoded rule sitting ABOVE
      the catalogue silently outranked it, so the one source that could have
      corrected it was the one being ignored. Positions come from the metafield
      now, with no anatomy override on top.
    */
    hip:       { label: "Hip", hour: 3, range: [2, 4], order: 2 },
    kidney:    { label: "Kidney", hour: 4.5,    order: 3 },
    sob:       { label: "Small Of Back", hour: 6, order: 4 },
    /*
      OFF THE BELT. Pablo 2026-09-28: pocket and purse are neither inside nor
      outside the waistband, so filing them under Inside was wrong. They are a
      third way of carrying, not a third clock position, which is why they have
      no hour and no diagram.

      NOTHING SUPPORTS THESE TODAY, so the Off The Belt segment hides itself.
      The Trigger Guard was the only style claiming them and it is IWB.

      CORRECTION to what was written here earlier: adding the off-body range is
      NOT just a POSITIONS entry. Pablo 2026-09-28: the Vice shoulder holster,
      the belly band, the purses and the sling bags are UNIVERSAL products, in
      the same sense as the four plain universals. Universals are deliberately
      kept out of the IWB and OWB pools so they cannot surface for a gun the
      catalogue molds, so an off-belt position on a universal style would never
      be reached through those pools. Surfacing them needs a universal-aware
      pool for this branch, not a table entry.
    */
    /*
      NOT A POSITION, AN OPT-OUT. Pablo 2026-09-28. It sits with the position
      tiles because that is where a shopper who does not know decides, but it
      applies NO filter: everything the waistband choice allows comes through.
      Ranked last so it never competes with a real answer.
    */
    any:       { label: "Not Sure / Show Me Everything", hour: null, order: 98 },
    purse:     { label: "Purse Or Bag", hour: null, order: 5, offBelt: true },
    pocket:    { label: "Pocket", hour: null,   order: 6, offBelt: true }
  };

  function hourText(h, left) {
    if (h === null) return null;
    var v = left ? (12 - h) % 12 : h;
    if (v === 0) v = 12;
    var whole = Math.floor(v);
    return v % 1 ? whole + " to " + (whole + 1) : String(v);
  }

  var DEFAULT_ORDER = [
    "IWB KYDEX Holster", "OWB Paddle KYDEX Holster", "Tuckable IWB KYDEX Holster",
    "IWB KYDEX Plus Line Holster", "Druid KYDEX Holster", "OWB Belt Loop KYDEX Holster",
    "Sidecar Tuckable IWB KYDEX Holster", "Athletic Wear Tuckable IWB Holster",
    "Trigger Guard Tuckable IWB KYDEX Holster", "X-FER Weapon Mounted Light Holster",
    /* Last, and only ever reachable through the Other path. */
    "LUX Universal Light Bearing Holster", "LUX V2 EXT Universal Light Bearing Holster",
    "Universal IWB Holster", "IWB Leather Holster"
  ];

  var BLURB = {
    "IWB KYDEX Holster": "Our most popular holster, with adjustable Posi-Click® retention and cant",
    "IWB KYDEX Plus Line Holster": "Comes with the Claw and a spring steel belt clip for less printing",
    "Tuckable IWB KYDEX Holster": "Clip lets you tuck in your shirt",
    "Sidecar Tuckable IWB KYDEX Holster": "Carries your gun and a spare mag in one rig",
    "Druid KYDEX Holster": "Two holsters in one: wear it inside or outside the waistband, Claw included, and 57% stronger than standard holsters",
    "Athletic Wear Tuckable IWB Holster": "Made for no-belt carry in gym shorts and joggers",
    "Trigger Guard Tuckable IWB KYDEX Holster": "Minimal trigger guard design for the smallest footprint",
    "X-FER Weapon Mounted Light Holster": "Designed to fit your light on any gun",
    "OWB Paddle KYDEX Holster": "Slides on and off without undoing your belt",
    "OWB Belt Loop KYDEX Holster": "Belt loops hold it tight and stable on the belt",
    /*
      The two universal styles. DELIBERATELY ABSENT from IWB and OWB above, so
      they can never surface as an alternate for a gun we DO mold; that is the
      finder's lesson, that a universal in front of someone who named their
      exact gun buries the holster built for it. They are reached only through
      the Other, Not Listed path.
      All of these lines are Pablo's, 2026-09-28. Two notes on them:

      "ALMOST any gun" for the LUX pair and "ANY gun" for the X-FER is a real
      distinction, not an inconsistency. Do not normalise them.

      The X-FER line REPLACES the canvas wording, "Universal fit by your light,
      not your gun", so the build and the approved canvas now differ here.

      And not "belt fit" or "fits by your belt", which was my coinage and is
      not something anyone says.
    */
    "LUX Universal Light Bearing Holster": "Designed to fit your light on almost any gun",
    "LUX V2 EXT Universal Light Bearing Holster": "Designed to fit your light on almost any gun",
    "Universal IWB Holster": "Fits 200+ Guns - Adjustable Retention",
    "IWB Leather Holster": "Leather, and Fits 200+ Guns"
  };

  /*
    SHIPS WITH A CLAW ALREADY, so offering the Claw Kit alongside sells the
    shopper a part they have. Gating on the Claw Compatible feature alone
    catches none of these, because all four ARE claw compatible.

    Plus Line and Druid: Pablo 2026-09-28.

    Sidecar and Trigger Guard Tuckable: Pablo 2026-09-30, and worth recording
    WHY rather than just that. They carry a DIFFERENT claw and it is included,
    so the catalogue flagging them Claw Compatible was right all along and the
    Claw Kit we sell would not fit them regardless. An earlier version of this
    read their flag as a data error and excluded them by hand; the flag was
    correct and the reasoning was not.
  */
  var CLAW_INCLUDED = [
    "IWB KYDEX Plus Line Holster",
    "Druid KYDEX Holster",
    "Sidecar Tuckable IWB KYDEX Holster",
    "Trigger Guard Tuckable IWB KYDEX Holster"
  ];

  /*
    A CATALOGUE ERROR, NOT A PRODUCT FACT. Pablo confirmed 2026-09-30 that the
    Universal IWB holster is not claw compatible, yet all 3 of its products
    carry the Claw Compatible flag in the generated data.

    THE FIX BELONGS IN THE CATALOGUE, not here. The same flag renders the
    "Claw Compatible" chip on the result card and feeds the PDP and the
    filters, so those three products currently advertise a claw that does not
    fit them everywhere they appear. This stops the quiz SELLING one; it
    cannot stop the chip. Delete this line once the products are corrected.
  */
  var CLAW_MISFLAGGED = ["Universal IWB Holster"];


  /*
    THIS STEP EXISTS TO SURFACE THE RANGE, not merely to sort it. Pablo
    2026-09-28: the Plus Line, the Druid and the Trigger Guard were reachable
    only as ties underneath "Keep It Simple", so the three products with the
    most to say for themselves were the ones a shopper never saw named.

    NO SUB-LABELS. Seven options with a line of explanation each is a wall of
    text, and each label now carries its own meaning. The disabled note stays,
    because "Not made for your gun yet" is information rather than decoration.

    "Lowest Price" is GONE, deliberately. It was the only option that sorted
    instead of matching, it pulled everyone who picked it toward the cheapest
    thing regardless of fit, and the live-price re-sort existed solely to
    serve it. Do not reinstate it as a shortcut for "just show me options".
  */
  var PRI_IWB = [
    { key: "simple", label: "Keep It Simple And Concealed",
      styles: ["IWB KYDEX Holster", "IWB KYDEX Plus Line Holster", "Druid KYDEX Holster", "X-FER Weapon Mounted Light Holster"] },
    { key: "conceal", label: "Max Concealability",
      styles: ["IWB KYDEX Plus Line Holster", "IWB KYDEX Holster"] },
    { key: "loaded", label: "All The Bells And Whistles",
      styles: ["Druid KYDEX Holster"] },
    { key: "minimal", label: "Minimalist Carry",
      styles: ["Trigger Guard Tuckable IWB KYDEX Holster"] },
    { key: "tuck", label: "Tuck My Shirt In Over It",
      styles: ["Tuckable IWB KYDEX Holster", "Trigger Guard Tuckable IWB KYDEX Holster", "Sidecar Tuckable IWB KYDEX Holster"] },
    { key: "mag", label: "Carry A Spare Mag Too",
      styles: ["Sidecar Tuckable IWB KYDEX Holster"] },
    { key: "nobelt", label: "No Belt Needed",
      styles: ["Athletic Wear Tuckable IWB Holster"] }
  ];
  /* The Druid ships with an OWB paddle as well as an IWB clip, so it is a
     genuine answer on this path too and earns the same option here. */
  var PRI_OWB = [
    { key: "quick", label: "Quick On And Off",
      styles: ["OWB Paddle KYDEX Holster"] },
    { key: "secure", label: "Tight And Stable On The Belt",
      styles: ["OWB Belt Loop KYDEX Holster"] },
    { key: "loaded", label: "All The Bells And Whistles",
      styles: ["Druid KYDEX Holster"] }
  ];

  /*
    APPROVED COPY, lifted verbatim from the canvas prototype. The canvas is the
    source of truth for wording; paraphrasing it here is how the page and the
    approved design drift apart. Kept in one object so it is auditable, and
    asserted verbatim by tools/test-holster-quiz-ranking.js.
  */
  var COPY = {
    /*
      The intro eyebrow "Rounded Gear Holster Finder" is GONE. Pablo
      2026-09-28: it read brown and "may not even be necessary". Both are true.
      It sat directly above a headline that says what the page is, on a page
      reached from an ad that already said it, and being small text it could
      not take the bright orange: at 4.5:1 the brightest legible orange is
      #c74c00, which is darker than the theme orange it was replacing. Deleting
      the line is a better answer than choosing between brown and illegible.

      COPY.matchH still uses the same class for "Your Match" on the top result
      card, where the label is doing work.
    */
    /*
      Front page copy. The promise is what they get, the proof is counted from
      the payload rather than asserted, and the reassurance answers the only
      objection a cold visitor has at the button: what is this going to cost me.
    */
    /* promise removed 2026-09-28: it restated the subhead at three lines,
       and on a phone those three lines were between the shopper and the
       button. The subhead carries the claim in one. */
    /*
      COUNTED, NOT CLAIMED. {n} is filled at render time from the payload.

      THE BIGGEST NUMBER THAT IS STILL TRUE, Pablo 2026-09-30. Three units were
      available and they are all real:

        269   gun models
        387   gun models times what is mounted under the barrel
        1124  those, times each holster that fits them

      1124 is every distinct answer the quiz can give: a gun, what is hanging
      under it, and a holster molded for that pair. Verified to contain no
      duplicates rather than assumed, since the same holster covering four
      Glocks appears under four models and each of those is a different answer.

      IT DOES NOT COUNT VARIANTS, which is where this would stop being true.
      Multiplying by draw hand and colour would give 4,496 and it would be
      padding: a left-handed black holster is not extra coverage, it is the
      same fit in a different box.

      THE PLUS IS ACCURATE, NOT DECORATION. Pablo 2026-09-30 asked for it to
      signal growth, and it also fixes a smaller problem: the count comes from
      the data snippet, which is a BUILD-TIME SNAPSHOT. New guns and new
      holsters go live between rebuilds, so the figure is already a floor that
      drifts behind the real catalogue. Without the plus the line is precisely
      wrong the day after a launch; with it, it is right until somebody
      deletes products.

      It self-corrects. The old line was a hand-written "300+", rounded UP past
      the 267 models then in the payload on the reasoning that universals cover
      guns we do not mold for. Sound reasoning, but the line could not notice
      the catalogue moving under it.
    */
    /*
      TWO PARTS, so the line breaks where it means something. One string would
      wrap wherever the width happened to run out, which on a phone put "Gun &"
      on the first line and orphaned "Holster Configurations" below it. These
      sit inline on a wide card and stack on a narrow one.
    */
    proofCount: "Coverage for {n}+",
    proofUnit: "Gun & Holster Configurations",
    reviews: "50,000+ 5-Star Reviews",
    /* The reassurance line under the button is gone, Pablo 2026-09-28. NOTE the
       live /pages/holster-finder meta description still says "No email
       required", so if an email step is ever added that description has to
       move with it. */
    /* The intro's three numbered steps went with them; COPY.steps had no other
       reader. The progress label still counts the steps for the shopper. */
    makeH: "What Make Is Your Gun?",
    makeHint: "The brand name is stamped on the side of the slide or grip.",
    modelHint: "Each holster is unique to the exact gun model, so it's important to verify your actual gun model.",
    /* modelSearch and modelEmpty went with the model search box, which went
       with the scrolling list. The picker does its own scrolling and every
       make has at least one model, so there is no empty state to word. */
    lightH: "Is Anything Mounted Under The Barrel?",
    /*
      THE FIT WARNING MOVED INTO THE BUBBLE, Pablo 2026-09-30. It had a band of
      its own below the question, with a tinted background and an orange rule,
      which cost a whole row on the step that already carries two questions.
      It is the same warning, read by anyone who opens the bubble, and the
      tiles now start where the band used to.
    */
    lightFitQ: "This matters for holster fit.",
    lightFitA: "A holster not designed to accommodate light or laser mounted under the barrel (or vice versa) will not fit.",
    /*
      THE HELP IS A BUBBLE ON THE HEADING, not a panel under the tiles. Pablo
      2026-09-28: folding it into the question removes a whole band of the
      screen, which is what was pushing the optic question down.

      lightHelpQ survives as the button's accessible name rather than as
      visible copy. It is the same sentence either way, so it stays pinned in
      the approved-copy list.
    */
    lightHelpQ: "Not sure what is on your gun?",
    lightHelpA: "If a light or laser is attached to the rail (below the barrel), the brand and model are printed on its side. If the rail is empty, choose No Rail Mounted Light/Laser.",
    /*
      THE TILE LABELS NAME THE PART, not the state. "Nothing Mounted" and "No
      Optic" both read as "no" to a shopper skimming four tiles on one screen,
      and the two questions are about different parts of the gun. Naming the
      rail and the slide in each answer is what keeps them apart once they
      share a screen. Pablo 2026-09-28.
    */
    railBare: "No Rail Mounted Light/Laser",
    /*
      OPTIC STEP. Shown from 2026-09-28 while the back-end flag is corrected;
      see OPTIC_FILTER for why it does not narrow the results yet.
    */
    opticAsk: "Do You Have A Slide Mounted Optic?",
    opticNone: "No Slide Mounted Optic",
    opticYes: "Yes, I have a Slide Mounted Optic",
    /* The chip, which is shorter than the answer because a chip is a reminder
       rather than a sentence, and "Optic Cut" is what it changes about the
       results: only cut holsters survive. */
    opticChip: "Optic Cut",
    opticNote: "A red dot or reflex sight mounted on top of the slide.",
    railLight: "Yes, I have a Rail Mounted Light/Laser",
    lightAsk: "Do You Have A Rail Mounted Light/Laser?",
    lightWhich: "Which Light/Laser Do You Have?",
    otherOpt: "Other, Not Listed",
    otherMakeQ: "Which Make Do You Carry?",
    /* {make} carries its own trailing space, so a missing make collapses the
       template to "Which Model Do You Carry?" rather than leaving a double
       space. It should never be missing: picking Other on the make step ends
       the quiz at the universal screen and never reaches a model. */
    otherModelQ: "Which Model {make} Do You Carry?",
    otherLightQ: "Which Light/Laser Do You Have?",
    otherCta: "Continue",
    universalH: "We Do Not Mold That One Yet",
    universalBody: "Nothing in the catalog is molded for that exactly. A universal holster fits a wide range of guns rather than one exact model.",
    waistH: "Do You Want To Carry Inside Or Outside Your Waistband?",
    /* Short forms: these are segments in a three-up toggle, about 110px wide
       on a 375px phone, and "Inside The Waistband" wraps to three lines there.
       The question directly above supplies the context, and the tiles below
       still name the position in full. */
    waistIn: "Inside",
    waistOut: "Outside",
    waistOff: "Off The Belt",
    waistUnsure: "Not Sure",
    carryH: "How Do You Want To Carry Your {gun}?",
    carryHint: "Most concealed carriers start inside the waistband. Options that do not exist for your gun are greyed out.",
    handH: "Which Hand Do You Draw With?",
    /* Labels the diagrams, which had a note under them and no question. */
    posH: "What Carry Position Do You Prefer?",
    diagramNote: "Diagrams show your waist from above. 12 o'clock is straight ahead.",
    /* No hint: seven options need the room, and the question is self-explanatory. */
    priH: "What Matters Most To You?",
    /*
      The gun bar's copy. "Finding Holsters for Your" rather than a bare label,
      because the sentence is what tells an arriving shopper what the page is
      doing on their behalf. It reads the same whether they chose the gun or an
      ad chose it for them.
    */
    gunLead: "Finding Holsters for Your ",
    gunChange: "Change",
    gunChangeLong: "Change gun make and model",
    matchH: "Your Match",
    whyH: "Why It Is Your Match",
    /*
      BUYING HAPPENS HERE NOW, Pablo 2026-09-30. "Shop This Holster" sent the
      shopper to a product page, which is the point the funnel used to end: the
      rest of the store starts there, and everything the quiz knows about them
      is thrown away at the door. The card configures and adds instead.

      shopCta is kept for the universal and empty outcomes, which still link
      out because there is nothing there to configure.
    */
    shopCta: "Shop This Holster",
    addCta: "Add To Cart",
    /* Never a blank button. B24 is the worked example: the theme's own Add to
       Cart hid its label for the length of the network round trip and an
       iPhone showed an empty white rectangle for a second and a half. */
    addBusy: "Adding...",
    addDone: "Added To Cart",
    addFail: "Could not add. Tap to try again",
    /* Named, so the button says which decision is outstanding rather than
       sitting disabled with no reason. The magazine holster is the case: 12
       variants across Model and Color, and the quiz never asked the calibre. */
    choosePrefix: "Choose ",
    soldOut: "Sold Out",
    /* The summary line's affordance. Deliberately a word rather than a
       pencil icon: the line reads as a statement of what is going in the
       cart, and "Change" is what makes it clear it can be. */
    specEdit: "Change",
    cartCheckout: "Secure Checkout",
    /* The fitment accordion's label. Not "Fits", per the copy rules. The
       count is appended once the list lands. */
    fitsH: "Compatible Guns",
    /* The Quick View. Shipping figures are the ones the cart drawer already
       states; returns link to the policy rather than restating its terms. */
    qvOpen: "View Details",
    qvClose: "Close",
    qvFull: "See Full Details",
    qvFeatures: "Features",
    qvSpecs: "Specs",
    qvShipping: "Shipping And Returns",
    qvShipLine: "Standard Shipping $6. Expedited 2-Day $15. Free Shipping On Orders Over $75, excluding partner-fulfilled products.",
    qvShipTime: "Ships same day if ordered before 1PM ET on a business day, otherwise the next business day.",
    qvShipLink: "Shipping Policy",
    qvReturnLink: "Return Policy",
    qvTrust: "Made In USA · Lifetime Warranty · Fast Shipping",
    cartItem: " item",
    cartItems: " items",
    altsH: "Also Molded For Your Gun",
    /* moreH is GONE, not merely unused: the "More Options For Your Gun"
       heading and the shorter card under it were removed 2026-09-30 so the
       results read as one ranked list. Left out of COPY entirely so nobody
       re-renders it from a string that is still sitting here. */
    addonsH: "Complete Your Setup",
    /* Shown instead of a Style picker on the Claw Kit, because the light
       answer decides the variant and confirming it is friction. It is a
       sentence, not a control. */
    addonWml: "The version made for your rail mounted light",
    restart: "Start Over With A Different Gun",
    /* The header has room for two words, not six. */
    restartShort: "Start Over",
    emptyH: "We Do Not Mold That Setup Yet",
    emptyBody: "There is no holster in the catalog for this gun and light combination today. Try a different carry position, or search the full catalog.",
    emptyCta: "Search All Holsters"
  };

  var CARRY_LABEL = { a: "Appendix", h: "Hip IWB", o: "Outside The Waistband", u: "Not Sure Yet" };
  /*
    "waist" was split out of "carry" on 2026-09-28. Outside The Waistband was
    the third of four tiles under "Where Will You Carry It?", where the first
    two options were both INSIDE positions, so the OWB range read as absent.
    It was never broken: the path produced Paddle, Druid and OWB Belt Loop
    correctly. It was buried, which for a shopper is the same thing.

    Inside-or-outside is a different question from where on the body, so it is
    now asked first and the position question only follows when the answer is
    inside.
  */
  /*
    The waist toggle's own keys, mapped to the carry keys the pools and filters
    use. They were assumed identical and are not: "out" is not "o".
  */
  var WAIST_CARRY = { "in": "i", "out": "o", "off": "off", "u": "u" };

  /*
    THE PRIORITY QUESTION IS HIDDEN. Pablo 2026-09-28: it does not make sense
    once the carry step asks what it asks. Hidden rather than deleted, behind
    one flag, because the ranking still uses a priority internally and the
    question may come back in a different form.

    With it off, the carry step goes straight to the results and ranked()
    falls back to the first priority in the list, which is the default order.
  */
  var SHOW_PRIORITY = false;

  /*
    THE OPTIC QUESTION IS ASKED, BUT IT DOES NOT FILTER YET.

    Pablo 2026-09-28: start showing it while the back end is updated. The
    question is worth asking and the answer is worth recording, but the
    filtering cannot switch on until the data is right, because the constraint
    is one-directional and unforgiving:

      no optic  -> every holster fits, cut or not. No filter, ever.
      has optic -> ONLY a cut holster fits. Hard filter.

    So the entire filter rides on the "has optic" branch, and that branch reads
    a flag Pablo has said is stale in the direction that matters: holsters that
    ARE cut are not all marked as such. Filtering on it today would hide the
    right holster from exactly the shopper who needs it.

    SWITCHED ON 2026-09-28, once both halves were true. The snippet was
    regenerated from a fresh export, and the generator now takes the optics bit
    from GUN_ATTRIBUTES.HAS_OPTICS rather than the marketing feature list,
    which disagreed with it on 320 of 641 holsters.

    What it means in practice, measured on the regenerated payload: 223 gun
    models have every holster optic compatible, 3 are mixed, and 43 have none.
    Those 43 now reach the empty state when a shopper says they run an optic.
    That is the honest answer: we do not make a cut holster for that gun, and
    saying so beats selling one that will not close over the sight.
  */
  var OPTIC_FILTER = true;

  var STEPS = SHOW_PRIORITY
    ? ["make", "model", "light", "carry", "pri", "results"]
    : ["make", "model", "light", "carry", "results"];

  /*
    WHAT EACH STEP OWNS, so going back to one can clear it and everything that
    depends on it.

    It has to be everything downstream, not just the step itself: a shopper who
    goes back to the make and picks a different one cannot keep the model, and
    a model with no light chosen for it cannot keep a carry position. The
    forward handlers already clear downstream state on every pick, for exactly
    this reason; this is the same rule applied to the backwards direction,
    which had been missed.

    The light step owns the optic answer too, because the optic question is
    revealed on that screen rather than owning one of its own.
  */
  var STEP_OWNS = {
    make:  { make: null, model: null, light: null, lightStage: "ask", optic: null,
             waist: null, carry: null, pos: null, pri: null, hand: null, allMakes: false },
    model: { model: null, light: null, lightStage: "ask", optic: null,
             waist: null, carry: null, pos: null, pri: null, hand: null },
    light: { light: null, lightStage: "ask", optic: null,
             waist: null, carry: null, pos: null, pri: null, hand: null },
    carry: { waist: null, carry: null, pos: null, pri: null, hand: null },
    pri:   { pri: null }
  };

  function clearFrom(step, patch) {
    var owned = STEP_OWNS[step];
    if (!owned) return patch;
    for (var k in owned) {
      if (Object.prototype.hasOwnProperty.call(owned, k)) patch[k] = owned[k];
    }
    return patch;
  }
  /*
    The QUESTIONS are the first five; "results" is an outcome, not a question.
    Counting it made the label read "Step 1 of 6" directly under an intro
    promising "4 quick steps". The denominator has to match the
    promise, so it is derived from STEPS rather than written as a literal that
    could drift if a step is ever added.
  */
  var QUESTION_COUNT = STEPS.length - 1;
  var NO_LIGHT = "No Light/Laser";

  /*
    Longest-first. "GlockStore" must be tested before "Glock" or every
    GlockStore model is filed under Glock.
  */
  var MAKES = ["Alpha Foxtrot", "Smith & Wesson", "SAR FIREARMS", "GlockStore", "Sig Sauer",
    "Springfield", "Hi-Point", "Staccato", "Browning", "Stoeger", "Beretta", "Walther", "Keltec",
    "Kimber", "Taurus", "Canik", "Glock", "Bersa", "Ruger", "Tisas", "KRISS", "SCCY", "Kahr",
    "FNH", "H&K", "IWI", "1911", "CZ"];

  /*
    POPULAR IS GONE, with TOP_MAKES before it.

    It was already demoted from an order to a badge when both lists went
    alphabetical on 2026-09-28. The badge then had nowhere to render once the
    model list became a native picker, because an option carries a label and
    nothing else, so the list itself became unreferenced. Removed rather than
    left in place: a dead list of 21 model names reads like configuration and
    invites someone to maintain it.

    Restoring the marker means suffixing labels, e.g. "Glock G19 (Gen 1-6) -
    Popular", which is noise on every popular row. The names are in the commit
    history if it is ever wanted back.
  */

  /* --------------------------------------------------------------- helpers */

  /*
    Numbers compare as numbers, so G17 < G19 < G43X < G117. Plain localeCompare
    sorts G117 between G17 and G19, which puts a shopper's model where they
    will not look for it.
  */
  function byName(a, b) {
    try { return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }); }
    catch (e) { return a.localeCompare(b); }
  }

  function money(n) { return "$" + Number(n).toFixed(2); }
  function commas(n) { return Number(n).toLocaleString("en-US"); }
  /*
    "(1 review)", not "(1 reviews)". Worth a function rather than a template:
    every product now carries a rating, and the catalogue really does contain
    products with exactly one, so the singular case is on screen rather than
    theoretical. The Druid fitted to the Glock 19 is the current example.
  */
  function reviewCount(n) {
    return "(" + commas(n) + " review" + (Number(n) === 1 ? "" : "s") + ")";
  }
  function makeOf(model) {
    for (var i = 0; i < MAKES.length; i++) {
      if (model === MAKES[i] || model.indexOf(MAKES[i] + " ") === 0) return MAKES[i];
    }
    return null;
  }
  function lightName(l) { return l === NO_LIGHT ? "Nothing Mounted (No Light/Laser)" : l; }

  /*
    A SHORTER NAME TO SHOW, WITHOUT LOSING THE ONE WE LOOK THINGS UP BY.

    Pablo 2026-09-30: drop the generations and the other trailing detail so the
    names read naturally. "Glock G19 (Gen 1-6)" is how the catalogue indexes a
    gun; it is not how anyone says it.

    TWO SUFFIXES ARE DROPPED and the rest are not, which is the whole care in
    this function:

      dropped   "(Gen 1-6)" and friends, and a trailing calibre group like
                "9MM/40SW/45ACP", neither of which a shopper needs to pick
                their own gun off a list
      kept      "(Non-Rail)", "(w/Rail)", "(MOD.2)" and anything else, because
                a rail is the difference between two holsters that do not
                interchange, and this is a fitment quiz

    AND THEN A UNIQUENESS GUARD OVER THE TOP, which is what makes it safe. Of
    270 models, 47 shorten and 8 of those would collide: Glock G22, G23 and G27
    each exist as Gen 1-4 and Gen 5-6 with different holsters, and a Smith &
    Wesson pair differs only by a calibre group. Those keep their full names,
    because two different guns reading identically in a list is how somebody
    buys a holster that does not fit.

    The map is built once from the data. The KEY is unchanged everywhere it
    matters: fitment lookups, URL matching and the analytics payload all still
    carry the catalogue's own string.
  */
  var DISPLAY = null;

  function buildDisplay(models) {
    /* Assigns as well as returns, so the one caller in production and the one
       in the test harness cannot end up with different maps. */
    var shortOf = {}, seen = {};
    models.forEach(function (m) {
      var s = m
        .replace(/\s*\(Gen[^)]*\)/i, "")
        .replace(/\s+[\d.]+(MM|SW|ACP|SIG)(\/[\d.]+(MM|SW|ACP|SIG))*\s*$/i, "")
        .trim();
      shortOf[m] = s || m;
      seen[s] = (seen[s] || 0) + 1;
    });
    var out = {};
    models.forEach(function (m) {
      out[m] = seen[shortOf[m]] === 1 ? shortOf[m] : m;
    });
    DISPLAY = out;
    return out;
  }

  function displayName(model) {
    if (!model) return model;
    return (DISPLAY && DISPLAY[model]) || model;
  }

  /*
    THE GUN GOES INTO THE QUESTIONS. Pablo 2026-09-30: keep tying the relevance
    through the quiz, so the light question asks about a rail mounted light on
    THEIR Glock G19X rather than in the abstract.

    A token rather than concatenation at each call site, because these are
    merchant-facing strings in COPY and an editor needs to be able to move the
    gun to a different part of the sentence without touching the renderer.

    THE FALLBACK MATTERS MORE THAN IT LOOKS. By the time any of these render
    the model is always known, so "Gun" should be unreachable. It is there
    because a heading reading "...On Your ?" would be a worse failure than a
    generic sentence, and this quiz has twice been bitten by a value that was
    assumed to be present.
  */
  function withGun(template, state) {
    var gun = (state && (displayName(state.model) || state.make)) || "Gun";
    return String(template).replace(/\{gun\}/g, gun);
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* ------------------------------------------------------------- analytics */

  /*
    B43 pattern. Dedupe on the SELECTION rather than "once per page": the
    finder's results event fired twice because its hydrate ran more than once,
    which halved every conversion rate measured against it and was invisible
    on screen. Null and empty params are dropped so GA4 parameter cardinality
    stays clean.
  */
  var lastEvent = {};
  function track(name, params, seedOnly) {
    try {
      var key = name + "|" + JSON.stringify(params || {});
      if (lastEvent[name] === key) return;
      lastEvent[name] = key;
      /* seedOnly records the payload as already sent without sending it. Used
         when saved results are restored: that completion was counted on the
         page load that earned it. */
      if (seedOnly) return;
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      if (params) {
        for (var k in params) {
          if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
          if (params[k] === null || params[k] === undefined || params[k] === "") continue;
          payload[k] = String(params[k]).slice(0, 100);
        }
      }
      window.dataLayer.push(payload);
    } catch (e) { /* measurement never breaks the buying path */ }
  }

  /* ------------------------------------------------------------------ quiz */

  function Quiz(root) {
    this.root = root;
    /* Tallest question height seen this session; see holdHeight(). */
    this._heldHeight = 0;
    this.mount = root.querySelector("[data-rg-hq-mount]");
    this.data = this.readData();
    if (!this.data || !this.mount) { this.fail(); return; }
    /* Built once, from the catalogue keys; see buildDisplay(). */
    buildDisplay(Object.keys(this.data.M));

    this.state = { step: "intro", make: null, model: null, light: null,
                   waist: null, carry: null, pos: null, pri: null, hand: null, q: "", allMakes: false,
                   /* light step is two parts: ask, then list. */
                   lightStage: "ask",
                   /* null until the optic question is answered. */
                   optic: null,
                   /* "Other, Not Listed" capture: which step, and what they typed. */
                   otherFor: null, otherText: "" };
    this.cache = {};
    this.readUrl();
    /*
      WHAT THE LINK ANSWERED, captured once, before the shopper touches
      anything. Sent as hq_prefill on holster_quiz_complete so deep-linked
      traffic can be told apart from shoppers who answered everything
      themselves. 2026-10-02.
    */
    this.prefilled = ["hand", "make", "model", "light", "optic", "carry"]
      .filter(function (k) { return this.state[k] !== null && this.state[k] !== ""; }, this);
    /* A link that answers anything wins over saved answers; see restoreState. */
    if (!this.prefilled.length) this.restoreState();
    this.personaliseHeadline();
    this.render();
  }

  /*
    ANSWERS SURVIVE A TRIP AWAY AND BACK, 2026-10-28. Clarity showed shoppers
    leaving the results for a product page and coming back to the front page,
    and Change or Back re-asked everything. The Facebook and Instagram in-app
    browsers, most of this page's traffic, often open a new-tab link in the
    same view, so Back there reloaded the quiz from scratch.

    sessionStorage, not the URL: it lasts for the tab and no longer, so a new
    visit or a fresh ad click starts clean, and the ad's URL parameters are
    never rewritten under the attribution tools reading them. A link that
    answers anything skips the restore entirely (see the constructor).

    Only the answers and the step are kept. The model is checked against the
    catalogue on the way back in, so a saved gun that a data rebuild removed
    cannot restore into a results page with nothing on it.
  */
  var SAVE_KEY = "rg-hq-state-v1";
  var SAVE_FIELDS = ["step", "make", "model", "light", "lightStage", "optic",
                     "waist", "carry", "pos", "pri", "hand"];
  var SAVE_MAX_AGE = 6 * 60 * 60 * 1000;

  Quiz.prototype.saveState = function () {
    try {
      var s = this.state, out = { t: Date.now() };
      SAVE_FIELDS.forEach(function (k) { out[k] = s[k]; });
      window.sessionStorage.setItem(SAVE_KEY, JSON.stringify(out));
    } catch (e) { /* private mode or blocked storage: the quiz still works */ }
  };

  Quiz.prototype.restoreState = function () {
    var saved;
    try { saved = JSON.parse(window.sessionStorage.getItem(SAVE_KEY) || "null"); } catch (e) { return; }
    if (!saved || typeof saved !== "object") return;
    if (!saved.t || Date.now() - saved.t > SAVE_MAX_AGE) return;
    if (!saved.step || saved.step === "intro") return;
    if (STEPS.indexOf(saved.step) < 0 && saved.step !== "results") return;
    if (saved.model && !this.data.M[saved.model]) return;
    /* The "Other" outcomes are not saved, so results without a gun would
       restore into the wrong page. Those shoppers start over instead. */
    if (saved.step === "results" && !saved.model) return;
    var s = this.state;
    SAVE_FIELDS.forEach(function (k) { if (k in saved) s[k] = saved[k]; });
    /* Results restored from storage were counted when they were earned. */
    this.restored = saved.step === "results";
  };

  Quiz.prototype.readData = function () {
    try {
      var node = this.root.querySelector(DATA_SELECTOR);
      if (!node) return null;
      var d = JSON.parse(node.textContent);
      return (d && d.M && d.P && d.S && d.L) ? d : null;
    } catch (e) { return null; }
  };

  /*
    Fail open. Without data the quiz cannot answer anything, so it shows a
    link to the catalogue rather than a dead shell. A cold visitor who lands
    on a broken lander is a wasted click that was paid for.
  */
  Quiz.prototype.fail = function () {
    if (this.root) this.root.setAttribute("data-rg-hq-failed", "true");
  };

  /*
    MATCHING A GUN NAME OUT OF A URL, FORGIVINGLY BUT NEVER BY GUESSING.

    The old prefill required the model parameter to equal a data key character
    for character, parenthetical generation range included. In practice that
    meant deep links did nothing: "Glock G19 (Gen 1-5)" misses, because the key
    is "(Gen 1-6)", and the miss was SILENT. The shopper landed on the intro as
    though no parameter had been passed, and the ad looked like it worked.

    Ad URLs are written by hand, so they need to tolerate "glock-19", "Glock
    19", "GLOCK%20G19". Normalising to letters and digits only does that:
    "Glock G19 (Gen 1-6)" and "glock 19" both reduce usefully, and the second
    is a prefix of the first once the make is stripped.

    WHAT IT WILL NOT DO IS PICK BETWEEN TWO CANDIDATES. "g19" is a prefix of
    both G19 and G19X, and on a fitment quiz the cost of choosing wrong is a
    holster that does not fit the gun the shopper actually owns. Ambiguity
    resolves to the MAKE, which lands them on the model list with a short list
    to tap. That is one tap, and it is honest.
  */
  function norm(v) {
    return String(v == null ? "" : v).toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  /*
    TOKENS, NOT ONE FLATTENED STRING, and the difference decides a real case.

    Flattening "Glock G19 (Gen 1-6)" to "glockg19gen16" makes "glock-g19" a
    prefix of BOTH the G19 and the G19X, so the one thing an ad URL is most
    likely to say comes out ambiguous and resolves to nothing. Split into
    ["glock","g19","gen","1","6"] the two are plainly different, because "g19"
    and "g19x" are different tokens.
  */
  function tokens(v) {
    return String(v == null ? "" : v).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }

  /*
    A token from the URL matches a token in the data when it is the same, or
    when the data's token is the same with ONE letter in front of it: "19"
    matches "g19", "365" matches "p365". Ad URLs drop that letter constantly,
    and it is the model designator's prefix rather than part of the number.
  */
  function tokenEq(want, key) {
    if (want === key) return true;
    return key.length === want.length + 1 &&
           key.charAt(0) >= "a" && key.charAt(0) <= "z" &&
           key.slice(1) === want;
  }

  /* Every token from the URL has to appear in the key, in order. Gaps are
     allowed, so "glock 19" matches "Glock G19 (Gen 1-6)" without the URL
     having to carry the generation range. */
  function tokensMatch(want, key) {
    var i = 0;
    for (var j = 0; j < key.length && i < want.length; j++) {
      if (tokenEq(want[i], key[j])) i++;
    }
    return i === want.length;
  }

  /*
    Returns the single matching key, or null when nothing matches OR when more
    than one does. Refusing to choose is the point: this is a fitment quiz, and
    a wrong gun sells a holster that does not fit. Ambiguity is handled by the
    caller, which falls back to asking.
  */
  function resolveOne(raw, keys) {
    if (!raw) return null;
    /*
      THE EXACT PATH IGNORES CASE AND SPACING, and that is not a convenience:
      it is what makes a hand-written ad link work.

      It used to be indexOf, so it matched only a value typed with the
      catalogue's own capitalisation. Nobody writes "?model=Sig%20Sauer%20P365"
      in a campaign builder; they write it lowercase. The lowercase version
      missed this path, fell through to token matching, matched the P365, the
      XL, the XL Spectre and six more, and resolved to NOTHING. Measured
      across the catalogue: 41 of 269 models were unreachable from a
      lowercase URL, including the Beretta APX, the CZ P01, the Canik TP9SF
      and every IWI Masada.

      IT CANNOT INTRODUCE AMBIGUITY. The 269 model keys are still 269 distinct
      strings when lowercased, verified, so a case-insensitive exact match
      lands on one key or none. The refusal-on-ambiguity rule below is
      untouched, and it is the one that protects fitment.
    */
    var flat = norm(raw);
    for (var k = 0; k < keys.length; k++) {
      if (norm(keys[k]) === flat) return keys[k];
    }
    var want = tokens(raw);
    if (!want.length) return null;
    var hits = [];
    for (var i = 0; i < keys.length && hits.length < 2; i++) {
      if (tokensMatch(want, tokens(keys[i]))) hits.push(keys[i]);
    }
    return hits.length === 1 ? hits[0] : null;
  }

  /*
    A model parameter usually arrives without its make ("19", "p365") or with
    it ("glock-19"). Both are tried: the plain value first, then the value with
    each make's name in front of it, which is what turns "19" into a unique
    match inside the Glock range rather than something that appears in a
    dozen model names across the catalogue.
  */
  function resolveModel(raw, models, make) {
    var hit = resolveOne(raw, models);
    if (hit) return hit;
    if (!raw) return null;
    var makes = make ? [make] : MAKES;
    for (var i = 0; i < makes.length; i++) {
      hit = resolveOne(makes[i] + " " + raw, models);
      if (hit) return hit;
    }
    return null;
  }

  /*
    URL prefill, so a per-model Meta ad can deep-link past questions it has
    already answered. Every value is validated against the data before it is
    accepted: a stale ad link naming a discontinued model must land the
    shopper on the first unanswered question, never on an empty result.
  */
  Quiz.prototype.readUrl = function () {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
    var s = this.state, D = this.data;

    var hand = (q.get("hand") || "").toUpperCase();
    if (hand === "L" || hand === "R") s.hand = hand;

    /*
      The make is resolved first and then used to disambiguate the model, so
      ?make=glock&model=19 lands on the G19 while ?model=19 on its own is
      ambiguous across the catalogue and deliberately does not.
    */
    var modelParam = q.get("model");
    s.make = resolveOne(q.get("make"), MAKES);
    var model = resolveModel(modelParam, Object.keys(D.M), s.make);
    if (model) {
      s.model = model;
      /* From the resolved key, never from the parameter: the key is the thing
         the rest of the quiz indexes by. */
      s.make = makeOf(model) || s.make;
    } else if (modelParam && !s.make) {
      /*
        The model did not resolve, but the parameter usually still NAMES the
        make: "Glock G19 (Gen 1-5)" is a real gun written with the wrong
        generation range, and the make in it is not in doubt. Keeping it turns
        a dead link into a short list to tap instead of a cold start.
      */
      var want = tokens(modelParam);
      for (var mi = 0; mi < MAKES.length; mi++) {
        if (tokensMatch(tokens(MAKES[mi]), want)) { s.make = MAKES[mi]; break; }
      }
    }

    var light = q.get("light");
    if (s.model && light) s.light = resolveOne(light, this.lightsFor(s.model));

    /*
      OPTIC, which the prefill used to skip past entirely. A link carrying a
      light sent the shopper to the carry step with the optic question never
      asked, and since an unanswered optic means no filter, they could be shown
      a holster with no optic cut without ever being asked whether they run one.
      Now the light step holds them until it is answered, which is exactly what
      that screen does for everyone else.
    */
    var optic = norm(q.get("optic"));
    if (optic === "yes" || optic === "y" || optic === "1" || optic === "true") s.optic = "yes";
    else if (optic === "no" || optic === "n" || optic === "0" || optic === "false") s.optic = "no";

    var carry = q.get("carry");
    if (s.light && carry && CARRY_LABEL[carry]) s.carry = carry;

    /*
      WHERE THE PREFILL LANDS. "pri" was the old destination for a link that
      carried a carry position, and it is NOT a step any more: the priority
      question was hidden, so STEPS does not contain it, header() bails on an
      index of -1 and no branch of renderStep matches. A link with ?carry= put
      the shopper on a BLANK SCREEN. It goes to the results, which is what the
      carry step itself does once answered.

      The light step now also holds anyone whose optic answer is missing, so a
      half-specified link asks the one question it is short of rather than
      quietly skipping it.
    */
    /*
      THE PREFILL NO LONGER PICKS A STEP. Pablo 2026-09-30: everyone lands on
      the front page, including a shopper arriving from a per-model ad. The
      page then greets them by their gun and the button skips whatever the link
      already answered.

      That is a better trade than dropping them into the middle of a quiz. The
      front page is where the proof and the premise are, it is the thing the ad
      promised, and arriving at "step 3 of 4" with a progress bar two thirds
      full is disorienting when you have answered nothing yourself.

      It also removes a whole class of bug rather than fixing one: the old code
      chose a step here and got it wrong twice, sending ?carry= links to "pri",
      which is not a step any more and rendered a blank screen, and skipping
      the optic question for anyone whose link carried a light. There is one
      place that decides where to start now, and it is firstUnanswered().
    */
  };

  /*
    The first question the quiz cannot answer from what it already knows. Used
    by the front page's button, so a prefilled shopper skips straight to it.

    The order is STEPS' order, and the conditions are the same ones each step
    writes: light owns the optic answer as well, because that question is asked
    on its screen.
  */
  /*
    The front page greets a prefilled shopper by their gun. The template comes
    from a section setting rather than from here, so the merchant owns the
    wording; see the note beside it in sections/rg-holster-quiz.liquid.

    It runs once, against the headline the section rendered, because that h1
    lives outside the mount and is not rebuilt between steps.
  */
  Quiz.prototype.personaliseHeadline = function () {
    var s = this.state;
    if (!s.make && !s.model) return;
    var h = this.root.querySelector("[data-rg-hq-headline]");
    if (!h) return;
    var tpl = h.getAttribute("data-rg-hq-headline-gun");
    if (!tpl || tpl.indexOf("{gun}") < 0) return;
    h.textContent = withGun(tpl, s);
  };

  Quiz.prototype.firstUnanswered = function () {
    var s = this.state;
    if (!s.make) return "make";
    if (!s.model) return "model";
    if (!s.light || !s.optic) return "light";
    if (!s.carry) return "carry";
    return "results";
  };

  Quiz.prototype.lightsFor = function (model) {
    var D = this.data, own = [], uni = [];
    var m = D.M[model] || {};
    Object.keys(m).forEach(function (k) { own.push(D.L[Number(k)]); });
    own.sort(function (a, b) {
      return a === NO_LIGHT ? -1 : b === NO_LIGHT ? 1 : a.localeCompare(b);
    });
    var u = D.M["Universal"] || {};
    Object.keys(u).forEach(function (k) {
      var l = D.L[Number(k)];
      if (l !== NO_LIGHT && own.indexOf(l) < 0) uni.push(l);
    });
    return own.concat(uni.sort());
  };

  /*
    The products that fit this model with this light, plus the universal
    light-fit X-FER holsters, which fit the LIGHT rather than the gun. A
    universal is only added when no dedicated holster of that style already
    covers the combination, so the gun's own holster always wins.
  */
  Quiz.prototype.available = function () {
    var D = this.data, s = this.state;
    if (!s.model || !s.light) return [];
    var li = D.L.indexOf(s.light);
    if (li < 0) return [];
    var self = this;
    var toProd = function (i) {
      var p = D.P[i];
      return { style: D.S[p[0]], price: p[1], handle: p[2],
               feats: D.F.filter(function (f, b) { return (p[3] >> b) & 1; }),
               rc: p[4], ra: p[5] };
    };
    var own = ((D.M[s.model] || {})[String(li)] || []).map(toProd);
    var uni = s.light === NO_LIGHT ? []
      : ((D.M["Universal"] || {})[String(li)] || []).map(toProd).filter(function (p) {
          return !own.some(function (o) { return o.style === p.style; });
        });
    return own.concat(uni);
  };

  /*
    The position is a filter, not a label. Choosing Small Of Back should not
    offer a Sidecar, which the catalogue lists for appendix only.
  */
  Quiz.prototype.poolFor = function (avail) {
    var s = this.state;
    var allow = s.carry === "o" ? OWB
              : (s.carry === "u" || s.carry === "off") ? IWB.concat(OWB) : IWB;
    var pos = s.pos;
    return avail.filter(function (p) {
      if (allow.indexOf(p.style) < 0) return false;
      /*
        An optic is the one fitment constraint with no give: a cut holster
        fits a gun without a sight, a gun WITH a sight does not fit an uncut
        one. So this filters in one direction only.
      */
      if (OPTIC_FILTER && s.optic === "yes" && !(p.feats && p.feats.indexOf("Optics Compatible") >= 0)) {
        return false;
      }
      if (!pos || pos === "any") return true;
      var list = (s.carry === "o" && POSITIONS_OWB[p.style]) || POSITIONS[p.style];
      return !list || list.indexOf(pos) >= 0;
    });
  };

  /* Positions this gun can actually be carried in, for the chosen waist. */
  Quiz.prototype.positionsFor = function (avail) {
    var s = this.state;
    var allow = s.carry === "o" ? OWB
              : (s.carry === "u" || s.carry === "off") ? IWB.concat(OWB) : IWB;
    var seen = {};
    var outside = s.carry === "o";
    avail.forEach(function (p) {
      if (allow.indexOf(p.style) < 0) return;
      var list = (outside && POSITIONS_OWB[p.style]) || POSITIONS[p.style] || [];
      list.forEach(function (k) {
        var p = POS[k];
        /* Off the belt is its own branch, and nothing else shows there. */
        if (s.carry === "off") { if (p.offBelt) seen[k] = true; return; }
        if (p.offBelt && s.carry !== "u") return;
        seen[k] = true;
      });
    });
    var keys = Object.keys(seen).sort(function (a, b) { return POS[a].order - POS[b].order; });
    /* Always offered, always last: not knowing is a valid answer. */
    if (keys.length) keys.push("any");
    return keys;
  };

  /* Label plus the clock reading for the chosen hand, e.g. "1 o'clock". */
  Quiz.prototype.posSub = function (key) {
    var p = POS[key], left = this.state.hand === "L";
    if (p.range) {
      /* Mirror both ends, then put them back in order: 2 to 4 becomes 8 to 10
         for a left hand draw, not 10 to 8. */
      var ends = p.range.map(function (h) {
        var v = (12 - h) % 12;
        return left ? (v === 0 ? 12 : v) : h;
      }).sort(function (a, b) { return a - b; });
      return ends[0] + " to " + ends[1] + " o'clock";
    }
    var h = hourText(p.hour, left);
    return h ? h + " o'clock" : null;
  };

  /*
    NOT SURE MEANS NOT SURE. Pablo 2026-09-28: "70% of users want an IWB
    holster, but let's not assume".

    This used to hand the unsure shopper PRI_IWB, the same list as someone who
    had chosen inside. The pool was already both, so OWB products were
    technically reachable, but every option they could pick preferred IWB
    styles, which left OWB ranking behind the 50-place penalty and showing up
    as a tail alternate at best. Offering a choice that cannot win is not
    offering it.

    The unsure list is now both, deduped by key, IWB first. Leading with IWB is
    not the assumption; it is the 70%. The difference is that "Quick On And
    Off" and "Tight And Stable On The Belt" are now on the screen, so a shopper
    who does not know the words "inside the waistband" can still land on a
    paddle by recognising what they want from it.

    "All The Bells And Whistles" is in both lists under one key, so the dedupe
    keeps the IWB entry and the Druid stays reachable either way.
  */
  Quiz.prototype.priorities = function () {
    var carry = this.state.carry;
    if (carry === "o") return PRI_OWB;
    if (carry !== "u") return PRI_IWB;
    var seen = {}, both = [];
    PRI_IWB.concat(PRI_OWB).forEach(function (p) {
      if (seen[p.key]) return;
      seen[p.key] = true;
      both.push(p);
    });
    return both;
  };

  Quiz.prototype.ranked = function () {
    var s = this.state;
    var pool = this.poolFor(this.available());
    var list = this.priorities();
    var pri = null;
    for (var i = 0; i < list.length; i++) if (list[i].key === s.pri) pri = list[i];
    if (!pri) pri = list[0];
    var pref = pri.styles;
    var rank = function (p) {
      var a = pref.indexOf(p.style);
      return (a >= 0 ? a : 50) * 100 + DEFAULT_ORDER.indexOf(p.style);
    };
    return pool.slice().sort(function (a, b) { return rank(a) - rank(b); });
  };

  /*
    APPENDIX IS 1 O'CLOCK, and 11 for a left hand draw. Pablo 2026-09-28; it
    read "1 to 2" and "10 to 11" before, which is a range rather than a
    position and put the marker nearer 2 on the diagram.

    The hip figures stay a range on purpose: 3 to 4 is genuinely where strong
    side carry sits, and unlike appendix it is not one agreed number.
  */
  Quiz.prototype.clock = function () {
    return this.state.hand === "L"
      ? { a: "11", h: "8 to 9", side: "left" }
      : { a: "1", h: "3 to 4", side: "right" };
  };

  /*
    Scroll ONLY when the step changes. It used to scroll on every state change,
    so toggling the hand or typing in the model search yanked the page. The
    sticky site header is handled by scroll-margin-top on .rg-hq in the CSS,
    which scrollIntoView respects, rather than by measuring the header here and
    depending on the theme's markup.
  */
  /*
    SCROLL ON ANY SCREEN CHANGE, NOT ONLY ON A STEP CHANGE.

    "Other, Not Listed" sets otherFor and leaves step alone, so the old
    condition never fired for it. The shopper had scrolled a long way down the
    make list to reach Other; the screen it opened is three elements tall, so
    the page kept the old scroll depth and left them below the content,
    looking at the footer.

    Tracking otherFor as well as step covers both directions, including Back,
    where step often patches to the value it already had.
  */
  Quiz.prototype.go = function (patch) {
    var wasStep = this.state.step;
    var wasOther = this.state.otherFor;
    for (var k in patch) if (Object.prototype.hasOwnProperty.call(patch, k)) this.state[k] = patch[k];
    /* Any change after a restore is the shopper's own, so a completion it
       leads to is a new one and is counted. */
    this.restored = false;
    this.saveState();
    this.render();
    var stepChanged = !!patch.step && patch.step !== wasStep;
    var otherChanged = Object.prototype.hasOwnProperty.call(patch, "otherFor") &&
                       patch.otherFor !== wasOther;
    if (stepChanged || otherChanged) {
      try { this.root.scrollIntoView({ block: "start", behavior: "smooth" }); } catch (e) {}
    }
  };

  /*
    THE BUILD-TIME PRICE IS FOR RANKING ONLY AND IS NEVER SHOWN.

    P[1] orders the "Lowest Price" results, because sorting a pool needs a
    price for every candidate and fetching the whole pool would be slow. What
    the shopper SEES always comes from /products/<handle>.js, so a price the
    snippet has gone stale on can never be displayed. Pablo, 2026-09-28.

    The consequence is worth knowing: if a price changes between builds, the
    "Lowest Price" ORDER can disagree with the live prices shown on the cards.
    The preflight's staleness check is the mitigation; regenerate the snippet
    when prices move.

    Failure is not silent-but-fine here either. A failed fetch hides the price
    AND the image and keeps only the Shop link, rather than showing a number
    that might be wrong. On a lander bought with cold traffic, a wrong price
    is worse than no price.
  */
  Quiz.prototype.hydrate = function (handle, ok, err) {
    if (this.cache[handle]) { ok(this.cache[handle]); return; }
    var self = this;
    fetch(PRODUCT_BASE + handle + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error("http")); })
      .then(function (j) {
        if (!j || !j.variants) return Promise.reject(new Error("shape"));
        self.cache[handle] = j; ok(j);
      })
      .catch(function () { if (err) err(); });
  };

  /*
    pickVariant, priceInfo and hrefFor are GONE, 2026-09-30. They existed to
    describe one variant to a product page we no longer send anybody to;
    defaultSelection and variantFor below decide the same thing for a card
    that sells where it stands.

    ONE BEHAVIOUR DELIBERATELY DID NOT SURVIVE, and it was a defect rather
    than a feature. pickVariant fell back to "the first available variant"
    when the shopper's hand was sold out, which on a two-hand product is the
    OTHER HAND. It then priced and linked that, so a left-handed shopper whose
    variant was out could be shown a right-handed holster at a right-handed
    price with nothing on screen saying so, and a wrong-hand holster is a
    return every time. The card now keeps their hand selected, strikes the
    value through and says Sold Out. A lost sale beats a wrong one.
  */

  /* ------------------------------------------------------- variants and cart

    The quiz configures and sells on this page rather than handing off to a
    product page. Everything below is the machinery for that.

    IT IS DRIVEN BY THE OPTION NAMES AND VALUES, not by product handles. A
    handle list would need editing every time the catalogue changes, and the
    quiz already carries 582 of them. Reading the roles off the option names
    means the same code configures a holster (Color, Hand), the Claw Kit
    (Style) and the magazine holster (Model, Color), and keeps working for a
    product nobody has thought about yet.
  */

  function optionRole(name) {
    var n = String(name || "").toLowerCase();
    if (n.indexOf("hand") >= 0) return "hand";
    if (n.indexOf("color") >= 0 || n.indexOf("colour") >= 0) return "color";
    return "other";
  }

  /*
    ONE SHAPE FOR THE OPTIONS, whatever product.js hands back.

    This store returns objects with name, position and values, and everything
    below is written against that. Shopify has also shipped a form where
    options is a plain array of NAMES with no values on it, which would leave
    every picker empty and every card unbuyable, silently: no error, just a
    card with a price and no button. Deriving the values from the variants
    when they are absent costs a few lines and removes a whole class of
    "worked in testing" failure.
  */
  function optionsOf(product) {
    var opts = (product && product.options) || [];
    return opts.map(function (o, i) {
      if (o && typeof o === "object" && o.values && o.values.length) {
        return { name: o.name, values: o.values, index: i };
      }
      var values = [];
      ((product && product.variants) || []).forEach(function (v) {
        var x = (v.options || [])[i];
        if (x != null && values.indexOf(x) < 0) values.push(x);
      });
      return { name: (o && o.name) || o, values: values, index: i };
    });
  }

  /*
    WHAT WE CAN ANSWER FOR THEM, AND WHAT WE MUST NOT GUESS.

    Hand and the Claw Kit's light variant are answers the quiz already has, so
    asking again is friction for no information. Colour follows whatever they
    picked for the holster, so a carbon fiber holster suggests a carbon fiber
    magazine carrier without forcing it.

    Model on the magazine holster is left NULL deliberately. It is the
    cartridge and stack, the quiz asks for the gun model and not the round it
    fires, and the mapping is not in the data. Guessing it would ship the
    wrong carrier and earn a return, so the button asks instead.
  */
  Quiz.prototype.defaultSelection = function (product) {
    var s = this.state, self = this;
    return optionsOf(product).map(function (opt, idx) {
      var values = opt.values || [];
      if (values.length === 1) return values[0];
      var role = optionRole(opt.name);

      if (role === "hand") {
        var want = s.hand === "L" ? "left" : "right";
        return first(values, function (v) { return v.toLowerCase().indexOf(want) >= 0; });
      }
      if (role === "color") {
        var liked = self.colorPref &&
          first(values, function (v) { return v === self.colorPref; });
        return liked || firstInStock(product, idx, values) || values[0];
      }
      /* Keyed on the VALUE, not on the product. Any accessory that offers a
         weapon-mounted-light variant gets the answer the shopper already gave
         on the light question. */
      var wml = first(values, function (v) {
        return v.toLowerCase().indexOf("weapon mounted light") >= 0;
      });
      if (wml) {
        var hasLight = s.light && s.light !== NO_LIGHT;
        return hasLight ? wml : first(values, function (v) { return v !== wml; }) || values[0];
      }
      /* The magazine carrier's size, from the holster's tags. Null when the
         tags do not resolve, which leaves the button asking. */
      return magValueFor(values, magSizeFromTags(self.gunTags));
    });
  };

  function first(list, fn) {
    for (var i = 0; i < list.length; i++) if (fn(list[i])) return list[i];
    return null;
  }

  /*
    MAGAZINE SIZE, READ OFF THE HOLSTER'S OWN TAGS. Pablo 2026-09-30.

    The store tags gun-specific products with the magazine's stack and
    calibre, so the carrier can be sized without asking the shopper for a
    cartridge they may not know. Surveyed across the whole catalogue rather
    than read off one product, because the vocabulary decides what is safe:

      DS 9MM        308   Double Stack, 9mm
      SS 380/9MM     53   AMBIGUOUS, see below
      SS Shirt       31   a SHORT SLEEVE SHIRT
      SS 45ACP       13   Single Stack, 45 ACP
      DS 45ACP       13   Double Stack, 45 ACP
      SS Multi        1   unknown

    TWO OF THE SIX MUST NOT BE MAPPED, which is why this is an explicit list
    and not a regex on the SS/DS prefix.

    "SS Shirt" is apparel. A prefix match reads it as Single Stack and puts a
    magazine carrier in the cart because somebody looked at a T-shirt.

    "SS 380/9MM" is genuinely ambiguous: the carrier sells "380 Single Stack"
    and "9MM/40SW Single Stack" separately and the tag spans both. Guessing
    the commoner would hand a Ruger LCP owner a 9mm carrier. It falls through
    and the shopper is asked, which is what happens today anyway.

    AN UNKNOWN TAG IS NOT A FAILURE, IT IS A QUESTION. Pablo flagged that the
    tags may have drifted and that newer models still need validating, so the
    safe direction is fixed here: anything not on this list resolves to null
    and the shopper is asked. Drift costs a question, never a wrong magazine.
    tools/audit-mag-tags.py reports which models resolve and which do not.
  */
  var MAG_TAGS = {
    "DS 9MM":   { stack: "Double Stack", cal: "9MM" },
    "SS 9MM":   { stack: "Single Stack", cal: "9MM" },
    "DS 45ACP": { stack: "Double Stack", cal: "45 ACP" },
    "SS 45ACP": { stack: "Single Stack", cal: "45 ACP" }
  };

  function magSizeFromTags(tags) {
    var found = null;
    (tags || []).forEach(function (t) {
      var hit = MAG_TAGS[String(t).toUpperCase().replace(/\s+/g, " ").trim()];
      if (hit) found = hit;
    });
    return found;
  }

  /* The carrier's own option value for that size, matched on the value text,
     so a renamed option value resolves to nothing rather than to the wrong
     one and the shopper is asked. */
  function magValueFor(values, size) {
    if (!size) return null;
    var cal = size.cal.replace(/\s+/g, "").toUpperCase();
    return first(values, function (v) {
      var u = v.toUpperCase();
      return u.indexOf(size.stack.toUpperCase()) >= 0 &&
             u.replace(/\s+/g, "").indexOf(cal) >= 0;
    });
  }

  function firstInStock(product, idx, values) {
    return first(values, function (v) {
      return (product.variants || []).some(function (x) {
        return x.available && (x.options || [])[idx] === v;
      });
    });
  }

  /* The variant matching a full selection. Null while any option is still
     unanswered, which is what keeps the Add button honest. */
  Quiz.prototype.variantFor = function (product, sel) {
    if (!product || !product.variants || !sel) return null;
    if (sel.some(function (v) { return v == null; })) return null;
    return first(product.variants, function (v) {
      return (v.options || []).every(function (o, i) { return o === sel[i]; });
    });
  };

  /*
    Whether a value is still reachable given the OTHER choices. A sold-out
    colour is shown struck through rather than hidden: hiding it makes the
    shopper wonder whether they misremembered, and a holster whose carbon
    fiber is out is still worth buying in black.
  */
  Quiz.prototype.valueReachable = function (product, sel, idx, value) {
    return (product.variants || []).some(function (v) {
      if (!v.available) return false;
      if ((v.options || [])[idx] !== value) return false;
      return sel.every(function (chosen, i) {
        return i === idx || chosen == null || v.options[i] === chosen;
      });
    });
  };

  /*
    THE CART IS SHOPIFY'S, NOT OURS. An earlier design held the whole kit in
    quiz state and wrote it in one go at checkout, which reads well and is
    wrong for two reasons that only show up later: nothing reaches the real
    cart, so Klaviyo has no abandoned cart to recover, and Elevar's
    add_to_cart never fires where it happened. Both are load-bearing revenue.
    So every Add writes immediately, exactly as the rest of the store does.

    REBUY IS THE CART AGAIN, 2026-10-17. This file was written while Rebuy's
    drawer was hidden on this page, and its own cart bar stood in for it. The
    hide was reverted on 2026-10-15 because the page had no working cart, and
    from then on the two competed: Rebuy opened its drawer on every add while
    the bar offered a second checkout that skipped Redo's free-returns offer
    and the upsells. Pablo's call: Rebuy is the cart, the bar is the fallback.

    The add itself is unchanged and still a plain fetch, so Klaviyo and Elevar
    see it as before. Rebuy patches fetch and XHR and opens its drawer on any
    add, measured on live at 1.18s on mobile, so the quiz does not open it.
    ensureDrawer() only steps in if that has not happened by DRAWER_WAIT.
  */
  Quiz.prototype.addToCart = function (items, ok, err) {
    var self = this;
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error("http")); })
      .then(function (j) { self.refreshCart(); self.ensureDrawer(); if (ok) ok(j); })
      .catch(function () { if (err) err(); });
  };

  /* Shopify's CDN resizes on a width parameter, on both the cdn.shopify.com
     and the /cdn/shop forms of a product image URL. */
  function sizedImage(src, w) {
    var u = src.indexOf("//") === 0 ? "https:" + src : src;
    return u + (u.indexOf("?") >= 0 ? "&" : "?") + "width=" + w;
  }

  /*
    REBUY, WHEN IT IS HERE AND READY TO BE CALLED. Checked every time rather
    than once, because Rebuy loads asynchronously and can arrive after the
    quiz has already rendered, most of all on a deep link that lands straight
    on the results.
  */
  var DRAWER_WAIT = 2500;
  function rebuy() {
    var R = window.Rebuy;
    return R && R.SmartCart && typeof R.SmartCart.show === "function" &&
      R.Cart && typeof R.Cart.fetchCart === "function" ? R : null;
  }
  function drawerOpen() {
    var R = rebuy();
    if (R && R.SmartCart.visible === true) return true;
    var b = document.body;
    return !!(b && b.classList && b.classList.contains("rebuy-cart-visible"));
  }

  /*
    THE SAFETY NET, NOT THE MECHANISM. If Rebuy has not opened its own drawer
    within DRAWER_WAIT of a successful add, make it re-read the cart and then
    show. The re-read comes first so the drawer never opens on a cart that
    does not have the new item in it. fetchCart returns false without calling
    back when Rebuy has a cart write queued, and its callback is not
    guaranteed, so show() is also called on a timer; the flag keeps it to one.

    Polled rather than checked once at the end, so a shopper who saw the
    drawer open on its own and closed it again is not shown it a second time.
  */
  Quiz.prototype.ensureDrawer = function () {
    var waited = 0, step = 250;
    (function poll() {
      if (drawerOpen()) return;
      if (waited < DRAWER_WAIT) { waited += step; setTimeout(poll, step); return; }
      var R = rebuy();
      if (!R) return; /* no Rebuy: the cart bar is already showing */
      var shown = false;
      var show = function () {
        if (shown) return;
        shown = true;
        try { R.SmartCart.show(); } catch (e) { /* the bar's fallback covers it */ }
      };
      try { if (R.Cart.fetchCart(show) === false) show(); } catch (e) { show(); }
      setTimeout(show, 1500);
    })();
  };

  /*
    CHECKOUT FROM THE DRAWER IS STILL A QUIZ CHECKOUT. holster_quiz_checkout
    used to fire only from the bar's button, so once the drawer became the
    cart it would have reported zero checkouts while people were checking out.

    Rebuy's button is .rebuy-cart__checkout-button. When Redo's free-returns
    block renders it replaces that with its own buttons inside
    redo-shopify-toggle, whose shadow root is CLOSED, so a click anywhere in
    it arrives here retargeted to the host. That includes Redo's info icon,
    so this can over-count by the odd tap on the icon. Measuring a checkout
    by a click is approximate either way; the real number is Shopify's.

    Results step only, as before: a checkout from the header icon on the
    front page did not come through the quiz.
  */
  Quiz.prototype.bindDrawerCheckout = function () {
    if (this.drawerCheckoutBound || !document.addEventListener) return;
    this.drawerCheckoutBound = true;
    var self = this;
    document.addEventListener("click", function (e) {
      if (self.state.step !== "results" || !drawerOpen()) return;
      var path = e.composedPath ? e.composedPath() : [];
      var hit = path.some(function (n) {
        return !!n && (n.tagName === "REDO-SHOPIFY-TOGGLE" ||
          (!!n.classList && n.classList.contains("rebuy-cart__checkout-button")));
      });
      if (!hit) return;
      var R = rebuy(), items = null, total = null;
      try { items = R.Cart.itemCount(); total = R.Cart.subtotal() / 100; } catch (err) { /* sent without */ }
      track("holster_quiz_checkout", { hq_from: "drawer", hq_items: items, hq_total: total });
    }, true);
  };

  Quiz.prototype.refreshCart = function (cb) {
    var self = this;
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error("http")); })
      .then(function (j) { self.cart = j; self.syncCartBar(); if (cb) cb(j); })
      .catch(function () { if (cb) cb(null); });
  };

  /* ---------------------------------------------------------------- render */

  Quiz.prototype.option = function (label, sub, onPick, opts) {
    opts = opts || {};
    var b = el("button", "rg-hq__opt" + (opts.selected ? " is-selected" : "") + (opts.other ? " rg-hq__opt--other" : ""));
    b.type = "button";
    if (opts.disabled) { b.disabled = true; b.className += " is-disabled"; }
    if (opts.icon) b.appendChild(opts.icon);
    var text = el("span", "rg-hq__opt-text");
    b.appendChild(text);
    text.appendChild(el("span", "rg-hq__opt-label", label));
    if (sub) text.appendChild(el("span", "rg-hq__opt-sub", sub));
    if (!opts.disabled) b.addEventListener("click", onPick);
    return b;
  };

  /*
    A carry diagram, cloned from the section's hidden originals.

    Only the inner group is mirrored for a left-hand draw. The clock numbers
    live outside it deliberately: mirroring them would render 3 and 9 back to
    front and place them on the wrong sides of the waist.
  */
  Quiz.prototype.diagram = function (key) {
    /*
      A position key reuses the appendix drawing and moves the marker to its
      own hour, so five positions need one template rather than five. Purse and
      Pocket have no hour and therefore no diagram.
    */
    var posKey = POS.hasOwnProperty(key) ? key : null;
    /* The opt-out borrows the question-mark drawing. Without it that tile had
       no icon and sat shorter than every other tile in the row, which is the
       shift Pablo saw on this step. */
    if (key === "any") return this.diagram("u");
    if (posKey && POS[posKey].hour === null) return null;
    var src = this.root.querySelector('[data-rg-hq-diagram="' + (posKey ? "a" : key) + '"]');
    if (!src) return null;
    var svg = src.cloneNode(true);
    svg.removeAttribute("data-rg-hq-diagram");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("class", "rg-hq__diagram");
    if (posKey) {
      var mark = svg.querySelector(".acc");
      if (mark) {
        var a = POS[posKey].hour * Math.PI / 6;
        mark.setAttribute("cx", (50 + 30 * Math.sin(a)).toFixed(1));
        mark.setAttribute("cy", (52 - 26 * Math.cos(a)).toFixed(1));
      }
    }
    var flip = svg.querySelector("[data-rg-hq-flip]");
    if (flip) flip.setAttribute("transform", this.flipTransform());
    return svg;
  };

  Quiz.prototype.flipTransform = function () {
    return this.state.hand === "L" ? "translate(100 0) scale(-1 1)" : "translate(0 0)";
  };

  /*
    HEADER GEOMETRY, and it is geometry rather than decoration.

    Three fixed rows, so the question below lands at the same offset on every
    step: the bar, then a row carrying "Step N of 5" on the left with Back
    right-aligned to the bar's end, then the chips on their own row.

    The chips row holds a CONSTANT height and does not wrap. It grows from one
    chip to four as the quiz proceeds, so a wrapping row moves the question
    down a line partway through, which is what read as the copy jumping
    around. It scrolls sideways instead; nothing is lost and the offset never
    changes.
  */
  /*
    THE CARD NEVER SHRINKS MID-QUIZ.

    The CSS reserve is a measured floor and it has now gone stale twice in one
    day, both times because a step's content changed after it was measured:
    once when the priority step went from five options to seven, once when the
    waistband question was split out of the carry step. A hardcoded number
    cannot survive that on its own.

    So the floor stays, for the first paint, and this keeps the tallest height
    actually seen in the session. Clearing min-height first is what makes the
    measurement the CONTENT height rather than the height we last forced.

    It cannot prevent one growth: a step taller than everything before it will
    still expand the card once, on its first visit. It removes every shrink,
    which is what reads as jumping, and it means a stale floor degrades to one
    step of movement instead of movement on every step.
  */
  Quiz.prototype.holdHeight = function () {
    var s = this.state;
    if (!this.mount || !this.mount.style) return;
    if (s.step === "intro" || s.step === "results") {
      this.mount.style.minHeight = "";
      return;
    }
    this.mount.style.minHeight = "";
    var h = this.mount.getBoundingClientRect ? this.mount.getBoundingClientRect().height : 0;
    if (h > this._heldHeight) this._heldHeight = h;
    if (this._heldHeight) this.mount.style.minHeight = Math.ceil(this._heldHeight) + "px";
  };

  /*
    ONE RESET, used by the link in the header and by the button on the results.
    They were separate patches and would have drifted: the results one already
    missed lightStage and the free-text capture, so starting over from there
    could land on the light LIST with no light chosen, or reopen the free-text
    screen.

    Goes to "make" rather than the intro: someone starting over wants the first
    question, not the sales pitch they have already read.
  */
  /*
    BACK TO THE FRONT PAGE, not the first question. Pablo 2026-09-28. It used
    to land on "make", on the reasoning that someone starting over wants the
    question rather than the pitch. That was wrong for the case that actually
    happens: starting over is a reset, and a reset that drops you mid-flow
    still feels like you are inside something you wanted out of.
  */
  Quiz.prototype.restart = function () {
    /*
      START OVER CLEARS THE MEASUREMENT STATE TOO, which the state patch below
      cannot reach because neither of these lives in state.

      prefilled is the more important of the two. It records what the LINK
      answered, and it exists so ad traffic that skipped questions can be told
      apart from shoppers who answered them. After a restart the shopper has
      answered everything themselves, so carrying the original link's list
      forward would attribute their work to the ad: the field would be wrong
      in exactly the comparison it was added for.

      _resultParams holds the last step's own params, hq_position among them.
      Left behind, a later completion reached by the universal or no-match
      route would report a carry position from a gun the shopper has since
      abandoned.
    */
    this.prefilled = [];
    this._resultParams = null;
    this.go({ step: "intro", make: null, model: null, light: null,
              lightStage: "ask", waist: null, carry: null, pos: null, pri: null,
              hand: null, q: "", allMakes: false, otherFor: null, otherText: "",
              otherResult: null });
  };

  Quiz.prototype.header = function (frag) {
    var s = this.state;
    var i = STEPS.indexOf(s.step);
    if (i < 0) return;
    var self = this;
    var head = el("div", "rg-hq__head");

    var barWrap = el("div", "rg-hq__bar");
    var bar = el("div", "rg-hq__bar-fill");
    var answered = Math.min(i + 1, QUESTION_COUNT);
    bar.style.width = (s.step === "results" ? 100 : Math.round((answered / QUESTION_COUNT) * 100)) + "%";
    barWrap.appendChild(bar);
    barWrap.setAttribute("role", "progressbar");
    barWrap.setAttribute("aria-valuemin", "1");
    barWrap.setAttribute("aria-valuemax", String(QUESTION_COUNT));
    barWrap.setAttribute("aria-valuenow", String(answered));
    head.appendChild(barWrap);

    var meta = el("div", "rg-hq__meta");
    meta.appendChild(el("p", "rg-hq__progress",
      s.step === "results" ? COPY.matchH : "Step " + (i + 1) + " of " + QUESTION_COUNT));
    /*
      BACK MUST CLEAR otherFor FIRST, OR IT DOES NOTHING VISIBLE.

      render() gives s.otherFor priority over s.step, so the free-text screen
      stays on display for as long as otherFor is set. Patching only the step
      therefore moved the shopper backwards invisibly: the "Step N of 5" label
      counted down while the screen never changed, and the only way out was to
      type something and press Continue, which also fires holster_quiz_other.
      So a mistaken tap on "Other, Not Listed" was a dead end AND a false
      signal about what we should mold next.

      Back from the free-text screen returns to the list it was opened from,
      which is the step named by otherFor, and clears what was typed so a
      stale value cannot be submitted later.
    */
    if (i > 0 || s.otherFor) {
      var back = el("button", "rg-hq__back", "Back");
      back.type = "button";
      back.addEventListener("click", function () {
        if (s.otherFor) {
          self.go({ otherFor: null, otherText: "", step: s.otherFor });
          return;
        }
        /*
          BACK CLEARS THE ANSWER IT RETURNS TO, and without that the shopper is
          trapped. Pablo 2026-09-30.

          The make, model and light pickers are native selects, and a select
          fires NO change event when you reopen it and choose the value it is
          already on. Back used to patch only the step, so the select came back
          still holding the old answer: picking the same thing again did
          nothing, and the only way forward was to pick something wrong and
          then pick the right one. Pressing Back to confirm a choice is a
          normal thing to do, so this was reachable without doing anything odd.

          Clearing is also the honest reading of the gesture: nobody presses
          Back on a question they are happy with. See clearFrom() for why
          everything downstream goes with it.
        */
        var target = STEPS[i - 1];
        self.go(clearFrom(target, { step: target }));
      });
      meta.appendChild(back);
    }
    /*
      START OVER, on every question rather than only on the results. Pablo
      2026-09-28. A shopper who picked the wrong make four steps ago should not
      have to press Back four times or finish a quiz they know is wrong.

      A link rather than a button: it sits beside Back and must not compete
      with it, and it is destructive, so it should not look like the obvious
      thing to press.
    */
    if (s.step !== "intro") {
      /*
        A PIPE BETWEEN THEM, and Back is a link now rather than a bordered
        button. Pablo 2026-09-30: the button was bumping into the gun bar
        below it.

        It had a border, a radius and its own padding, which made it the
        heaviest thing in a row whose job is to be quiet, and it sat directly
        above a panel with its own border. Two boxes stacked a few pixels
        apart read as a collision. As two links either side of a rule they are
        a pair, which is also what they are.

        The separator is aria-hidden: it is punctuation between two controls
        that already have names, and a screen reader announcing "line" between
        them would be noise.
      */
      if (meta.querySelector(".rg-hq__back")) {
        meta.appendChild(el("span", "rg-hq__sep", "|")).setAttribute("aria-hidden", "true");
      }
      var over = el("button", "rg-hq__over", COPY.restartShort);
      over.type = "button";
      over.addEventListener("click", function () { self.restart(); });
      meta.appendChild(over);
    }
    head.appendChild(meta);

    /*
      THE CHIPS ARE ANSWERS, NOT PLACEHOLDERS. Pablo 2026-09-30: no "Not Sure
      Yet" bubbles on the match page.

      A chip is there to remind the shopper what the results were built from.
      "Not Sure Yet" was built from nothing: it is the opt-out that says show
      me everything, so printing it back adds a line of text that narrows
      nothing and reads like a setting they failed to fill in. The waistband
      opt-out is the same, which is why the inside/outside chip is dropped
      rather than shown as undecided.

      Everything else is kept: make and model, light, hand, waistband and
      position. They are what the ranking actually used.
    */
    /*
      THE GUN GETS A BAR, NOT A BUBBLE. Pablo 2026-09-30, and deep links are
      what forced it.

      A shopper who starts at the beginning watched themselves choose the gun,
      so a small grey chip was enough to remind them. A shopper who arrives
      from an ad at step three never chose anything: the gun in that chip is an
      assertion the page is making about them, and it decides every answer
      below it. If it is wrong and they cannot see it, they answer three
      questions about a gun they do not own.

      So it says what it is doing in words, names the gun in the page's own
      voice, and carries a Change control. Change goes back to the make step
      and clears the answers underneath, because a different gun means a
      different model and a different fit.

      It appears as soon as there is anything to show, including on the model
      step where only the make is known, which is where a ?make= link lands.
    */
    var chips = el("div", "rg-hq__chips");
    var add = function (t) { if (t) chips.appendChild(el("span", "rg-hq__chip", t)); };
    if (i >= 3) add(s.light === NO_LIGHT ? NO_LIGHT : s.light);
    /*
      OPTIC CUT, but only when they have an optic. Pablo 2026-09-30.

      This is the one answer on the screen that HARD filters: with an optic
      only a cut holster fits, so it removes results. Saying so is the
      difference between a short list and a short list whose reason is visible.

      "No optic" is not printed, for the same reason "Not Sure Yet" is not. It
      narrows nothing: every holster fits a gun without one.
    */
    if (s.optic === "yes") add(COPY.opticChip);
    if (i >= 4) {
      /* "u" is the opt-out on both toggles: no waistband preference and no
         position preference. Nothing to report, so nothing is printed. */
      if (s.waist === "in") add("IWB");
      else if (s.waist === "out") add("OWB");
      if (s.carry && s.carry !== "u") add(CARRY_LABEL[s.carry]);
      if (s.hand) add(s.hand === "L" ? "Left Hand" : "Right Hand");
    }
    if (s.make || s.model) {
      var gun = el("div", "rg-hq__gun");
      var gunText = el("p", "rg-hq__gun-label");
      gunText.appendChild(el("span", "rg-hq__gun-lead", COPY.gunLead));
      gunText.appendChild(el("span", "rg-hq__gun-name", displayName(s.model) || s.make));
      gun.appendChild(gunText);

      var change = el("button", "rg-hq__gun-change", COPY.gunChange);
      change.type = "button";
      change.setAttribute("aria-label", COPY.gunChangeLong);
      change.addEventListener("click", function () {
        self.go(clearFrom("make", { step: "make" }));
      });
      gun.appendChild(change);
      /*
        THE CHIPS LIVE IN THE BAR NOW. Pablo 2026-09-30: on desktop they belong
        inside the gun box rather than on a row of their own below it, which
        also lifts everything above the fold by a row.

        Change is appended BEFORE them on purpose. On a phone the bar wraps to
        [gun name | Change] and then the chips underneath; on desktop the CSS
        reorders so the chips sit between the name and Change, which is the
        arrangement that leaves the longest gun names room to breathe.
      */
      gun.appendChild(chips);
      head.appendChild(gun);
    } else {
      /* No gun yet, on the make step, so there is no bar to put them in. There
         are no chips at that point either; it is appended for the reserved
         height that keeps the question from moving between steps. */
      head.appendChild(chips);
    }

    frag.appendChild(head);
  };

  Quiz.prototype.heading = function (frag, title, hint) {
    frag.appendChild(el("h2", "rg-hq__q", title));
    if (hint) frag.appendChild(el("p", "rg-hq__hint", hint));
    return frag.lastChild;
  };

  /*
    AN INFO BUBBLE ON A HEADING, replacing the collapsible help panel that used
    to sit under the light tiles.

    WHY IT OVERLAYS RATHER THAN PUSHES. The card reserves a fixed height per
    step so nothing below it moves while the shopper answers. A panel that
    opened in the flow would either grow the card or force the reserve to cover
    a state most people never open. Absolute positioning costs neither: the
    bubble paints over the tiles and the layout underneath never knows.

    IT IS NOT STATE. Opening it does not go through go(), because a re-render
    rebuilds this node and would close it on the way. It toggles a class on
    nodes it owns, which also means it cannot affect the measured height.
  */
  /*
    SECTIONS, not one title and one body. The bubble now carries the fit
    warning as well as the how-to-check, because the warning used to have a
    tinted band of its own below the question and that band cost a row on the
    step that already asks two questions.

    labelText stays the button's accessible name and is no longer assumed to
    be the first thing inside, so the name can describe the control while the
    contents lead with whichever section matters most.
  */
  function infoBubble(labelText, sections) {
    var wrap = el("span", "rg-hq__info-wrap");
    var btn = el("button", "rg-hq__info", "i");
    btn.type = "button";
    btn.setAttribute("aria-label", labelText);
    btn.setAttribute("aria-expanded", "false");
    var pop = el("div", "rg-hq__info-pop");
    pop.setAttribute("role", "note");
    pop.setAttribute("hidden", "hidden");
    sections.forEach(function (sec) {
      if (sec[0]) pop.appendChild(el("p", "rg-hq__info-title", sec[0]));
      if (sec[1]) pop.appendChild(el("p", "rg-hq__info-body", sec[1]));
    });

    var open = false;
    function set(next) {
      open = next;
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) { pop.removeAttribute("hidden"); wrap.classList.add("is-open"); }
      else { pop.setAttribute("hidden", "hidden"); wrap.classList.remove("is-open"); }
    }
    btn.addEventListener("click", function (e) {
      if (e && e.stopPropagation) e.stopPropagation();
      set(!open);
    });
    /* Tapping anywhere else closes it, which is how every other bubble on a
       phone behaves, and Escape closes it for a keyboard. Both are guarded
       because the DOM test shim has neither document-level events nor keys. */
    if (typeof document !== "undefined" && document.addEventListener) {
      document.addEventListener("click", function () { if (open) set(false); });
      document.addEventListener("keydown", function (e) {
        if (open && e && (e.key === "Escape" || e.keyCode === 27)) set(false);
      });
    }
    wrap.appendChild(btn);
    wrap.appendChild(pop);
    return wrap;
  }

  Quiz.prototype.render = function () {
    var self = this, s = this.state, D = this.data;
    /*
      The hero is an intro, not a header. Repeating the image, headline and
      subhead above every question pushed the answers off a phone screen and
      made each step appear to jump, because the block above them re-rendered
      at a slightly different height. It stays in the DOM for the h1 and is
      hidden by CSS from the first question onward.
    */
    this.root.classList.toggle("rg-hq--started", s.step !== "intro");
    /*
      The card holds ONE height across every question, so nothing below it
      moves as the shopper answers. CSS owns the number; this only says which
      screen is showing. Released at the results, where the content genuinely
      varies with how many holsters matched and a reserve would be arbitrary.
    */
    this.root.classList.toggle("rg-hq--results", s.step === "results");
    /*
      THE CART IS READ ONCE ON ARRIVAL AT THE RESULTS, not on every render.
      A shopper can reach this page with a cart already in it, from an earlier
      visit or another tab, and the bar has to say so rather than claiming the
      cart is empty until they add something. Every later add refreshes it
      itself. Kept off the question steps because there is nothing to buy
      there and it is a request that would compete with the product fetches.
    */
    if (s.step === "results" && !this.cartRead) {
      this.cartRead = true;
      this.refreshCart();
      this.bindDrawerCheckout();
      this.bindCartSync();
    }
    this.syncCartBar();
    /* The carry step unfolds as it is answered, so it reserves its END state
       rather than the first-paint floor every other step uses. The light step
       does the same now that the optic question is revealed on it rather than
       taking a screen of its own. */
    this.root.classList.toggle("rg-hq--step-carry", s.step === "carry");
    this.root.classList.toggle("rg-hq--step-light", s.step === "light");
    var frag = document.createDocumentFragment();
    this.header(frag);

    /*
      "Other, Not Listed" takes the step over rather than appearing beneath the
      list. The shopper has already told us their gun is not there; showing
      them the list again underneath a text box is just the same dead end with
      extra steps.
    */
    if (s.otherFor) this.renderOther(frag, s.otherFor);
    else this.renderStep(frag, s, D, self);

    this.mount.innerHTML = "";
    this.mount.appendChild(frag);
    this.holdHeight();

    /*
      Anything that has to find a node by selector runs HERE, after the
      fragment is in the document. Calling fill() from inside renderResults
      queried this.mount while the cards were still in a detached fragment, so
      it silently found nothing and no image or price ever appeared. It looked
      like a Lowest Price feature, because that path calls fill() from an async
      callback that happens to run after the mount.
    */
    var queued = this._afterMount || [];
    this._afterMount = [];
    for (var qi = 0; qi < queued.length; qi++) queued[qi]();
  };

  Quiz.prototype.afterMount = function (fn) {
    (this._afterMount = this._afterMount || []).push(fn);
  };

  /*
    "Other, Not Listed" on make, model and light.

    Two reasons this exists and only one of them is the shopper's. It stops an
    outlier hitting a dead end, and it records WHAT they typed, which is the
    only signal the catalogue gets about guns and lights people want and we do
    not mold. Read it in GA4 as hq_other_make, hq_other_model, hq_other_light.
  */
  /*
    THE THREE LONG LISTS ARE NATIVE SELECTS. Pablo's call 2026-09-28.

    They were tappable lists, then tappable lists with an internal scroll cap,
    and the cap was the mistake: the shopper scrolled the list AND the page,
    which is a hybrid nobody works out. Measured at 375x812 the make pane ends
    142px above the fold, so it looks fine in an emulator; on a real iPhone vh
    resolves against the LARGE viewport, ignoring the URL bar, so the same
    48vh overflows what you can actually see. That is the reported symptom.

    A native control removes the question rather than tuning it. The picker is
    the platform's own, it cannot nest inside the page scroll, and the quiz
    keeps a constant short height, which also settles the scroll-position
    problem for every screen rather than just these.

    Carry and priority keep their cards deliberately: four options each, with
    diagrams, and they are the steps that make this feel like a quiz.

    OTHER_VALUE is a sentinel rather than an empty string because an empty
    value is also what the placeholder carries, and the two must not collide.
  */
  var OTHER_VALUE = "\u0000other";

  Quiz.prototype.select = function (cfg) {
    var self = this;
    var sel = el("select", "rg-hq__select");
    sel.setAttribute("aria-label", cfg.label);

    /* Disabled, so it can be shown but never chosen back. */
    var ph = el("option", null, cfg.placeholder);
    ph.value = "";
    ph.setAttribute("disabled", "disabled");
    if (!cfg.value) ph.setAttribute("selected", "selected");
    sel.appendChild(ph);

    cfg.groups.forEach(function (g) {
      if (!g.items.length) return;
      var parent = sel;
      if (g.label) {
        parent = el("optgroup", null, null);
        parent.setAttribute("label", g.label);
        sel.appendChild(parent);
      }
      g.items.forEach(function (it) {
        var o = el("option", null, it.label);
        o.value = it.value;
        if (cfg.value === it.value) o.setAttribute("selected", "selected");
        parent.appendChild(o);
      });
    });

    var other = el("option", null, COPY.otherOpt);
    other.value = OTHER_VALUE;
    sel.appendChild(other);

    sel.addEventListener("change", function () {
      var v = this.value;
      if (v === OTHER_VALUE) { self.go({ otherFor: cfg.kind, otherText: "" }); return; }
      if (v) cfg.onPick(v);
    });
    return sel;
  };

  /* otherOption() was the tappable "Other, Not Listed" row appended to each
     list. select() now appends it as the last option of every picker, so the
     helper had no callers left. The free-text screen it opened is unchanged. */

  Quiz.prototype.renderOther = function (frag, kind) {
    var self = this, s = this.state;
    var q = kind === "make" ? COPY.otherMakeQ
          : kind === "model" ? COPY.otherModelQ.replace("{make} ", s.make ? s.make + " " : "")
          : COPY.otherLightQ;
    /*
      NO HELPER TEXT. Pablo's call 2026-09-28, after walking all three of these
      screens: the shopper typing a gun we do not mold does not need to be told
      what we will do with it. The question and the box are the whole screen.
    */
    this.heading(frag, q, null);

    var input = el("input", "rg-hq__search");
    input.type = "text";
    input.value = s.otherText || "";
    input.setAttribute("aria-label", q);
    input.addEventListener("input", function () { s.otherText = this.value; });
    frag.appendChild(input);

    var go = el("button", "rg-hq__cta", COPY.otherCta);
    go.type = "button";
    go.addEventListener("click", function () {
      var text = (s.otherText || "").trim().slice(0, 80);
      if (!text) return;   /* nothing to record, and nothing to act on */
      var params = { hq_step: "other_" + kind, hf_make: s.make, hf_model: s.model };
      params["hq_other_" + kind] = text;
      track("holster_quiz_other", params);
      self.go({ otherFor: null, otherResult: { kind: kind, text: text }, step: "results" });
    });
    frag.appendChild(go);
    return frag;
  };

  Quiz.prototype.renderStep = function (frag, s, D, self) {
    if (s.step === "intro") {
      /*
        THE FRONT PAGE SELLS THE QUIZ, IT DOES NOT JUST OPEN IT.

        It was three numbered lines and a button. The order now puts the payoff
        before the work: what the shopper gets, then the proof that it is worth
        answering, then the three steps, then the ask.

        The catalogue line is the only new claim and it is computed from the
        payload, not asserted, so it cannot go stale in a direction that
        overstates: it counts the gun models actually in the data.
      */
      /*
        THE BUTTON COMES FIRST, everything else explains it afterwards.

        This page had the headline, a subhead, a promise paragraph, a proof
        line and three numbered steps stacked ABOVE the button, and on a phone
        that put the one thing the page exists for below the fold. The subhead
        and the promise also said the same thing twice.

        So: hero, headline, one line, button. The steps and the proof still
        earn their place, they just earn it BELOW, where they answer "what am I
        about to do" for the shopper who did not press immediately.
      */
      var start = el("button", "rg-hq__cta rg-hq__cta--start",
                     this.root.getAttribute("data-rg-hq-cta") || "Find My Holster");
      start.type = "button";
      start.addEventListener("click", function () {
        track("holster_quiz_start", { hq_hand: s.hand });
        /*
          STRAIGHT TO THE FIRST THING WE DO NOT KNOW. Pablo 2026-09-30: a deep
          link should not drop the shopper into the middle of a quiz, it should
          land them on the front page like everyone else and then skip the
          questions it has already answered for them.

          So the prefill no longer chooses the step; the button does. A visitor
          with no parameters gets "make", which is what it always did.
        */
        self.go({ step: self.firstUnanswered() });
      });
      frag.appendChild(start);

      /*
        COUNTED FROM THE PAYLOAD: every gun model, times what can hang under
        its barrel, times each holster molded for that pair. That is every
        distinct answer this quiz can give.

        The Universal bucket is excluded. It is not a gun anybody owns, and
        the holsters in it are already counted under the guns they fit.

        Falls back to the models alone if the shape ever changes, because a
        line reading "Coverage for 0" is worse than a smaller true number.
      */
      var combos = 0, models = 0;
      Object.keys(D.M || {}).forEach(function (m) {
        if (m === "Universal") return;
        models += 1;
        var byLight = D.M[m] || {};
        Object.keys(byLight).forEach(function (k) {
          combos += (byLight[k] || []).length;
        });
      });
      var proofLine = el("p", "rg-hq__proof-line");
      proofLine.appendChild(el("span", "rg-hq__proof-count",
        COPY.proofCount.replace("{n}", commas(combos || models))));
      /* A REAL SPACE, not a CSS margin. The two spans are adjacent in the DOM,
         so without it the text reads "1,124+Gun" to a screen reader and to
         anyone who copies the line. A leading space inside a block collapses
         on its own when the spans stack, so it costs nothing there. */
      proofLine.appendChild(el("span", "rg-hq__proof-unit", " " + COPY.proofUnit));
      frag.appendChild(proofLine);

      /*
        REAL GOLD STARS, Pablo 2026-09-28, under the coverage line. Filled star
        glyphs rather than the trust bar's 15px outline icon, because at this
        size the stars ARE the claim and an outline reads as decoration.

        The figure is the one the homepage hero and the site footer already
        carry: 57,000+ product plus site reviews, with Amazon on top, so
        50,000+ is a floor rather than a stretch.

        The stars are marked aria-hidden and the sentence carries the meaning,
        so a screen reader hears the claim once rather than five stars first.
      */
      var rev = el("p", "rg-hq__reviews-line");
      var stars = el("span", "rg-hq__stars-gold", "\u2605\u2605\u2605\u2605\u2605");
      stars.setAttribute("aria-hidden", "true");
      rev.appendChild(stars);
      rev.appendChild(el("span", "rg-hq__reviews-text", COPY.reviews));
      frag.appendChild(rev);

      /*
        THE THREE NUMBERED STEPS ARE GONE. Pablo 2026-09-28: they add no value
        right now. They described the quiz to someone who can simply start it,
        and they were the tallest thing between the headline and the button.
      */
    }

    if (s.step === "make") {
      this.heading(frag, COPY.makeH, COPY.makeHint);
      /*
        Alphabetical, all of them, no model counts. Pablo's call after QA,
        2026-09-28, and a deliberate change from the prototype, which led with
        eight popular makes behind a Show All button. A shopper arrives knowing
        their make and is looking it up, not browsing, so one predictable list
        beats a ranked one plus a disclosure. The counts answered a question
        nobody asks.
      */
      var seen = {};
      Object.keys(D.M).forEach(function (m) {
        var mk = makeOf(m); if (mk) seen[mk] = true;
      });
      frag.appendChild(this.select({
        kind: "make",
        label: COPY.makeH,
        placeholder: "Select your gun make",
        value: s.make,
        groups: [{ items: Object.keys(seen).sort(byName).map(function (m) {
          return { value: m, label: m };
        }) }],
        onPick: function (m) {
          self.step("model", { make: m, model: null, light: null, waist: null, carry: null, pos: null, pri: null, q: "" }, { hf_make: m });
        }
      }));
    }

    if (s.step === "model") {
      this.heading(frag, "What Model " + s.make + " Is Your Gun?", COPY.modelHint);
      /*
        The search box went with the list. It existed to make a long scroll
        bearable; the picker has its own scrolling and, on desktop, type-ahead.
        Median models per make is 6 and only Sig, Smith and Glock exceed 29.
      */
      frag.appendChild(this.select({
        kind: "model",
        label: "What Model " + s.make + " Is Your Gun?",
        placeholder: "Select your model",
        value: s.model,
        /* Label shortened, VALUE untouched: the value is the catalogue key
           everything else indexes by. */
        groups: [{ items: this.modelsFor(s.make).map(function (m) {
          return { value: m, label: displayName(m) };
        }) }],
        onPick: function (m) {
          self.step("light", { model: m, light: null, waist: null, carry: null, pos: null, pri: null, lightStage: "ask" }, { hf_model: m });
        }
      }));
    }

    if (s.step === "light") {
      /*
        ONE SCREEN THAT UNFOLDS, not two screens. Pablo 2026-09-28: the optic
        question belongs on the light screen, hidden until the light question
        is answered.

        It shipped as its own screen first, on the reasoning that two parts of
        the gun deserve two screens or people mis-answer both. The reasoning
        was not wrong about the drawings, which is why they are still two
        drawings with two headings; it was wrong about the screens. Under the
        rail and on top of the slide are ONE question about what is bolted to
        the gun, and splitting them cost a full screen transition in the middle
        of it while making the step counter disagree with itself: five screens
        under a "Step 3 of 4" label.

        So all of it lives here, revealed in order:

          rails                         always
          which light (select)          only when the Light/Laser rail is on
          optic question                only once the light answer resolves

        Nothing resolves the step any more. Only the optic answer advances, and
        it is the last thing revealed, so the shopper cannot leave with one of
        the two unanswered.

        THE RAILS STAY TAPPABLE AFTER THEY ARE ANSWERED, which is the point of
        putting them on one screen: correcting the light no longer means going
        Back past a question you had already answered.
      */
      /* The bubble now carries BOTH the fit warning and the how-to-check, so
         the whole of this step is question, tiles, question, tiles. The fit
         warning's tinted band below the heading is gone; it was a row of
         chrome on the one step that asks two questions. */
      var lightH = el("h2", "rg-hq__q", COPY.lightAsk);
      lightH.appendChild(infoBubble(COPY.lightHelpQ, [
        [COPY.lightFitQ, COPY.lightFitA],
        [COPY.lightHelpQ, COPY.lightHelpA]
      ]));
      frag.appendChild(lightH);

      var listOpen = s.lightStage === "list";
      var rails = el("div", "rg-hq__rails");
      [["bare", COPY.railBare, false], ["light", COPY.railLight, true]].forEach(function (r) {
        /* The light rail reads as chosen from the moment it opens the list,
           before a light is picked. Otherwise the list appears from nowhere
           with neither tile marked. */
        var picked = r[2] ? listOpen : s.light === NO_LIGHT;
        var b = el("button", "rg-hq__rail" + (picked ? " is-selected" : ""));
        b.type = "button";
        var svg = self.diagram(r[0]);
        if (svg) b.appendChild(svg);
        b.appendChild(el("span", "rg-hq__rail-cap", r[1]));
        b.addEventListener("click", function () {
          /* Switching rails CLEARS the light. Leaving a previously chosen
             light in place while Nothing Mounted is lit would filter the
             results by a light the shopper has just said they do not have. */
          if (r[2]) self.go({ lightStage: "list", light: null });
          else self.go({ light: NO_LIGHT, lightStage: "ask" });
        });
        rails.appendChild(b);
      });
      frag.appendChild(rails);

      if (listOpen) {
        /*
          THE TWO GROUPS CARRY WHAT THE SUBTITLES USED TO.

          An option can hold a label and nothing else, so "Holster molded for
          your gun with this light" versus "Fits by the light with our
          universal X-FER holster" had nowhere to go. That distinction is not
          decoration: a holster molded for the gun beats a universal, and a
          shopper choosing between two lights should be able to see which one
          lands them on which. Two optgroups say the same thing in the picker
          itself, and say it once rather than on every row.
        */
        var own = Object.keys(D.M[s.model] || {}).map(function (k) { return D.L[Number(k)]; });
        var molded = [], universal = [];
        this.lightsFor(s.model).forEach(function (l) {
          if (l === NO_LIGHT) return;   /* answered by the drawing above */
          (own.indexOf(l) >= 0 ? molded : universal).push({ value: l, label: l });
        });
        frag.appendChild(this.select({
          kind: "light",
          label: COPY.lightWhich,
          placeholder: "Select your light or laser",
          value: s.light,
          groups: [
            { label: "Molded For Your Gun", items: molded },
            { label: "Universal Fit By Your Light", items: universal }
          ],
          onPick: function (l) { self.go({ light: l }); }
        }));
      }

      /*
        THE SECOND QUESTION, REVEALED. Same gun rendering with the optic on the
        top rear of the slide, so there is no confusion about which part of the
        gun is being asked about.

        The gate is a resolved light, which is either rail: NO_LIGHT from the
        bare drawing, or a real light from the select. A light of null with the
        list open is the one state in between, and it keeps this hidden.
      */
      if (s.light) {
        /*
          SAME HEADING TREATMENT AS THE LIGHT QUESTION. Pablo 2026-09-28: it was
          16px ink under a 26px orange question, which read as a note attached
          to the light question rather than as the second question on the
          screen. It is an h3 for the document outline and an .rg-hq__q for the
          eye, which is the combination that makes it read as a peer.
        */
        frag.appendChild(el("h3", "rg-hq__q rg-hq__q--second", COPY.opticAsk));
        frag.appendChild(el("p", "rg-hq__hint", COPY.opticNote));
        var optics = el("div", "rg-hq__rails");
        [["bare", COPY.opticNone, "no"], ["optic", COPY.opticYes, "yes"]].forEach(function (o) {
          /*
            A class of its own, because there are four rails on this screen and
            "the last two" is not something a test or a stylesheet should have
            to count.

            THESE NEVER RENDER AS SELECTED, and that is the fix for what Pablo
            reported twice. They used to take is-selected when s.optic matched,
            which reads as reasonable and cannot be right: answering this
            question calls step("carry") and leaves the screen in the same
            tick, so a ringed optic tile is never feedback. It can only be
            stale state from a visit that already ended, and it made the screen
            look finished while the shopper still had to act on it.

            The path in was answering the optic question and pressing Back.
            Back clearing its own step closes that route; this closes the whole
            class, whatever puts a value there. The LIGHT rails above keep
            their ring, because the shopper stays on this screen after
            answering them and the ring is the only record of what they said.
          */
          var b = el("button", "rg-hq__rail rg-hq__rail--optic");
          b.type = "button";
          var svg = self.diagram(o[0]);
          if (svg) b.appendChild(svg);
          b.appendChild(el("span", "rg-hq__rail-cap", o[1]));
          b.addEventListener("click", function () {
            self.step("carry", { optic: o[2], waist: null, carry: null, pos: null, pri: null },
                      { hf_undercarriage: s.light, hq_optic: o[2] });
          });
          optics.appendChild(b);
        });
        frag.appendChild(optics);
      }
    }

    if (s.step === "carry") {
      var avail = this.available();
      var hasAny = function (styles) {
        return avail.some(function (p) { return styles.indexOf(p.style) >= 0; });
      };
      var iwbOk = hasAny(IWB), owbOk = hasAny(OWB);
      var clk = this.clock();

      /*
        ONE STEP, TWO TOGGLES, AND THE TILES BELOW FOLLOW THEM.

        Inside-or-outside was briefly its own step. Pablo's call 2026-09-28 is
        better: it belongs here as a toggle alongside Hand, because the two
        answers together decide what is worth showing, and a shopper can flip
        between them and watch the positions change instead of going back a
        screen to find out.

        It is a toggle rather than four tiles because that is what buried OWB
        in the first place: it was the third tile under a heading whose first
        two options were both inside positions. A segmented control puts the
        choice in front of the shopper before any position is described.

        Not Sure is a third segment rather than a missing answer. Pablo:
        "70% of users want an IWB holster, but let's not assume." Inside leads
        because it is the 70%; the other two are one tap away, not one step.
      */
      this.heading(frag, withGun(COPY.carryH, s), COPY.carryHint);

      frag.appendChild(el("h3", "rg-hq__sub-q", COPY.waistH));
      var wt = el("div", "rg-hq__hand rg-hq__hand--segmented");
      /*
        The Off The Belt segment is shown only when something actually offers
        an off-belt position. It is empty today and hides itself; it returns on
        its own once the off-body range is indexed. A dead segment that leads
        to an empty screen is worse than no segment.
      */
      var offOk = avail.some(function (p) {
        return (POSITIONS[p.style] || []).some(function (k) { return POS[k].offBelt; });
      });
      [["in", COPY.waistIn, iwbOk], ["out", COPY.waistOut, owbOk]]
        .concat(offOk ? [["off", COPY.waistOff, true]] : [])
        .concat([["u", COPY.waistUnsure, iwbOk || owbOk]]).forEach(function (w) {
        var b = el("button", "rg-hq__hand-btn" + (s.waist === w[0] ? " is-selected" : ""), w[1]);
        b.type = "button";
        b.setAttribute("aria-pressed", s.waist === w[0] ? "true" : "false");
        if (!w[2]) { b.disabled = true; b.className += " is-disabled"; }
        /* Clearing carry matters: the positions below differ per side, so a
           stale one would survive a flip and be submitted with the wrong set. */
        /*
          WAIST KEYS AND CARRY KEYS ARE NOT THE SAME, and conflating them is
          what made Outside show the inside positions: the toggle set carry to
          "out" while every filter compares against "o", so Outside fell
          through to the inside branch. Mapped explicitly here, once.
        */
        /*
          RIGHT HAND IS PRESELECTED, 2026-10-28, reversing the 2026-09-28 call
          noted below. About 85% of customers draw right handed, so making
          everyone answer it cost a tap for most and read as a dead step. It is
          set here, when the hand question is revealed, rather than in state
          from the start, so a link's own hand (or one already chosen) wins and
          the prefill measurement still records only what a link answered.
        */
        b.addEventListener("click", function () {
          self.go({ waist: w[0], carry: WAIST_CARRY[w[0]], pos: null, hand: s.hand || "R" });
        });
        wt.appendChild(b);
      });
      frag.appendChild(wt);

      /*
        The hand question waits for the waistband answer, the same way the
        positions wait for both. Asking all three at once is three questions
        under one heading; revealed in order it is one question that unfolds.
      */
      if (!s.waist) return;

      frag.appendChild(el("h3", "rg-hq__sub-q", COPY.handH));
      var toggle = el("div", "rg-hq__hand");
      [["R", "Right Hand"], ["L", "Left Hand"]].forEach(function (h) {
        var b = el("button", "rg-hq__hand-btn" + (s.hand === h[0] ? " is-selected" : ""), h[1]);
        b.type = "button";
        b.setAttribute("aria-pressed", s.hand === h[0] ? "true" : "false");
        b.addEventListener("click", function () { self.go({ hand: h[0] }); });
        toggle.appendChild(b);
      });
      frag.appendChild(toggle);

      /*
        THE POSITIONS WAIT FOR BOTH ANSWERS. Pablo 2026-09-28.

        They depend on both: the waistband choice decides WHICH positions
        exist, and the draw hand decides which side of the body every one of
        them is on. Showing them before either is answered means showing
        something that is about to change, and a default selection is not a
        choice, which is why neither toggle starts with one. SUPERSEDED for the
        hand on 2026-10-28: it now arrives preselected as Right Hand with the
        waistband answer (see the waistband buttons above), so in practice the
        positions appear as soon as the waistband is chosen.
      */
      if (!s.waist || !s.hand) return;

      frag.appendChild(el("h3", "rg-hq__sub-q", COPY.posH));
      frag.appendChild(el("p", "rg-hq__note", COPY.diagramNote));

      /*
        THE POSITIONS COME FROM custom.carry_position, via the POSITIONS table.

        They used to be two hardcoded tiles, appendix and a strong-side hip,
        which the catalogue does not actually offer on any IWB style. Now the
        tiles are whatever the styles available for this gun and this side of
        the waistband genuinely support, so Outside shows Hip alone because
        that is all the Paddle and the Belt Loop list, and a Trigger Guard gun
        can offer Purse and Pocket.
      */
      var posKeys = this.positionsFor(avail);
      var cList = el("div", "rg-hq__opts rg-hq__opts--carry");
      posKeys.forEach(function (k) {
        cList.appendChild(self.option(POS[k].label, self.posSub(k), function () {
          self.step(SHOW_PRIORITY ? "pri" : "results", { pos: k, pri: null },
                    { hq_carry: s.carry, hq_position: k, hq_hand: s.hand });
        }, { selected: s.pos === k, icon: self.diagram(k) }));
      });
      if (!posKeys.length) {
        cList.appendChild(el("p", "rg-hq__empty", "Not made for your gun yet"));
      }
      frag.appendChild(cList);
    }

    if (s.step === "pri") {
      this.heading(frag, COPY.priH, null);
      var availP = this.available();
      var hasP = function (styles) {
        return availP.some(function (p) { return styles.indexOf(p.style) >= 0; });
      };
      var pList = el("div", "rg-hq__opts");
      this.priorities().forEach(function (p) {
        var ok = hasP(p.styles);
        pList.appendChild(self.option(p.label, ok ? null : "Not made for your gun yet", function () {
          self.step("results", { pri: p.key }, { hq_priority: p.key });
        }, { selected: s.pri === p.key, disabled: !ok }));
      });
      frag.appendChild(pList);
    }

    if (s.step === "results") this.renderResults(frag);

  };

  /*
    Alphabetical, numbers compared as numbers, so G17 < G19 < G43X < G117.
    Popular-first ordering was removed earlier: it made the list unpredictable
    for anyone whose gun was not on that list, which is most of the catalogue.

    THE "Popular" BADGE IS GONE TOO, and that is a real loss rather than an
    oversight: a native option carries a label and nothing else. It marked
    browsing, and this step is lookup, so it was the cheapest thing to drop
    when the list became a picker. Putting it back means suffixing the label,
    which reads as noise on every popular row.
  */
  Quiz.prototype.modelsFor = function (make) {
    return Object.keys(this.data.M)
      .filter(function (m) { return makeOf(m) === make; })
      .sort(byName);
  };

  /* One place that advances a step, so every step emits exactly one event. */
  Quiz.prototype.step = function (next, patch, params) {
    patch = patch || {};
    patch.step = next;
    var s = this.state;
    var base = { hq_step: next, hf_make: patch.make || s.make, hf_model: patch.model || s.model };
    for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) base[k] = params[k];
    /*
      COMPLETE IS NO LONGER SENT FROM HERE. It used to be, which meant a link
      that pre-answered every question reached the results without ever
      calling step("results"), and holster_quiz_complete never fired: found
      2026-10-02 testing ?make=glock&model=19&light=no+light&optic=no&carry=h.
      The last step's own params (e.g. hq_position) are kept and sent by
      trackComplete() from renderResults, which every route to results passes
      through.
    */
    if (next === "results") this._resultParams = base;
    else track("holster_quiz_step", base);
    this.go(patch);
  };

  /*
    ONE holster_quiz_complete for every route to the results: answered by hand,
    pre-answered by a deep link, the "Other" universal outcome, or no match.
    It carries the FULL answer set from state, not just the last step's
    params, because answers a link pre-filled were never sent as step events
    and GTM's data layer cannot be trusted to remember them. track() dedupes
    an identical payload, so a re-render does not double count.
  */
  Quiz.prototype.trackComplete = function (extra) {
    var s = this.state;
    var p = { hq_step: "results", hf_make: s.make, hf_model: s.model,
              hf_undercarriage: s.light, hq_optic: s.optic, hq_carry: s.carry,
              hq_position: s.pos, hq_hand: s.hand, hq_priority: s.pri,
              hq_prefill: (this.prefilled && this.prefilled.length) ? this.prefilled.join(",") : "none" };
    var k, last = this._resultParams || {};
    for (k in last) if (Object.prototype.hasOwnProperty.call(last, k) && last[k] != null) p[k] = last[k];
    for (k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) p[k] = extra[k];
    /* Restored results were already counted; see restoreState(). Seeding the
       dedupe rather than skipping once keeps a re-render of the same results
       from sending it either. */
    track("holster_quiz_complete", p, !!this.restored);
  };

  /*
    RESULTS, and the one place live data can change the ORDER.

    For every priority except Lowest Price the order is decided by style, so
    the cards paint immediately and each price and image fills in as its fetch
    lands. For Lowest Price the order IS the price, so painting first and
    reordering afterwards would move the top pick under the shopper's thumb.
    That case waits for the four fetches, with a timeout so a slow or blocked
    request cannot leave the page empty.

    Only the four displayed cards are re-sorted. Which four were chosen still
    comes from the build-time price, because sorting the pool needs a price for
    every candidate. Pricing is set per style rather than per gun, so the pool
    rarely drifts; see docs/holster-quiz.md.
  */
  Quiz.prototype.renderResults = function (frag) {
    var self = this, s = this.state;

    /*
      The "Other" outcome. A universal fits a wide range of guns rather than
      one exact model, so it is the honest answer for a gun we do not mold.

      Only the three X-FER light-fit holsters reach the data today. The four
      plain universals (universal-iwb-holster, leather-universal-holster,
      grip-tac-holster, smooth-ride-light-duty-iwb-leather-holster) carry NO
      holster_style metafield, so the generator drops them. Until that is set
      this path can only help someone who named a light. It says so plainly
      rather than pretending, and it still records what they typed.
    */
    if (s.otherResult) {
      this.heading(frag, COPY.universalH, COPY.universalBody);
      var uni = [];
      var U = this.data.M["Universal"] || {};
      Object.keys(U).forEach(function (k) {
        U[k].forEach(function (i) {
          var p = self.data.P[i];
          if (!uni.some(function (x) { return x.handle === p[2]; })) {
            uni.push({ style: self.data.S[p[0]], price: p[1], handle: p[2],
                       feats: self.data.F.filter(function (f, b) { return (p[3] >> b) & 1; }),
                       rc: p[4], ra: p[5] });
          }
        });
      });
      if (uni.length) {
        /* Cheapest first. Someone whose gun we do not mold is being offered a
           compromise, so the lowest cost of entry leads. */
        uni.sort(function (a, b) { return a.price - b.price; });
        var show = uni.slice(0, 4);
        var uwrap = el("div", "rg-hq__results");
        show.forEach(function (p, i) { uwrap.appendChild(self.card(p, i, i === 0)); });
        frag.appendChild(uwrap);
        this.afterMount(function () { self.fill(show); });
      }
      var link = el("a", "rg-hq__cta", COPY.emptyCta);
      link.href = "/search";
      frag.appendChild(link);
      this.trackComplete({ hq_top_style: "universal", hq_other_kind: s.otherResult.kind });
      return;
    }

    var ranked = this.ranked();
    if (!ranked.length) {
      self.heading(frag, COPY.emptyH, COPY.emptyBody);
      var link = el("a", "rg-hq__cta", COPY.emptyCta);
      link.href = "/search";
      frag.appendChild(link);
      self.trackComplete({ hq_top_style: "none" });
      return;
    }

    var cards = ranked.slice(0, 4);
    this.trackComplete({ hq_top_style: cards[0] && cards[0].style });
    var wrap = el("div", "rg-hq__results");
    frag.appendChild(wrap);

    var paint = function (list) {
      wrap.innerHTML = "";
      wrap.appendChild(self.card(list[0], 0, true));
      if (list.length > 1) {
        /*
          GOOD, BETTER, BEST. The top three get full cards; everything else
          goes into a sideways scroller.

          Sideways on purpose: a capped vertical list inside a page that also
          scrolls is the hybrid that was removed from the question steps
          earlier today, and it reads no better here. A horizontal rail is a
          familiar "more like this" pattern and cannot fight the page.
        */
        /*
          ONE LIST, NOT TWO. Pablo 2026-09-30: drop the "More Options For Your
          Gun" heading and let the rest of the holsters sit with the ones above
          them.

          They were a separate band with its own heading and a shorter card,
          which said "these are the leftovers" about holsters the ranking had
          already judged a fit. The top card still leads, by being the top card;
          below it the order is the ranking's and every card looks the same, so
          a shopper comparing three is comparing three of the same thing rather
          than two proper results and a footnote.

          The compact modifier went with the heading. It was the other half of
          the same signal, and it was also what squeezed the star rating into a
          narrower column than the cards above.
        */
        for (var i = 1; i < list.length; i++) {
          wrap.appendChild(self.card(list[i], i, false));
        }
      }
      self.addons(wrap, list[0]);
      var again = el("button", "rg-hq__restart", COPY.restart);
      again.type = "button";
      again.addEventListener("click", function () { self.restart(); });
      wrap.appendChild(again);
    };

    /*
      One path now. The second one existed only for "Lowest Price", which
      resolved all four prices before painting anything, showed a "Checking
      prices" line while it waited and re-sorted on what came back. With that
      option gone the order is decided by style alone, so the cards paint
      immediately and each price and image fills in as its own fetch lands.
    */
    paint(cards);
    this.afterMount(function () { self.fill(cards); });
  };

  /* byLivePrice and livePrice went with "Lowest Price". They sorted the four
     displayed cards on what the live fetches came back with, which no
     remaining priority needs; every other order is decided by style. */

  /*
    THE BUY BLOCK: every option the product has, the live price for the
    variant those options resolve to, and one Add button. Used by the holster
    cards and by the accessories, which is the point of driving it off option
    names rather than handles.

    It cannot be rendered until the product fetch lands, because only that
    carries the options and the prices. So it appends into a slot the card
    reserved, and the reserve is what stops it shifting the page underneath a
    thumb when it arrives.

    THE PRICE IS THE VARIANT'S, never the product's and never the build-time
    table's. Carbon fiber is $3 more than black on the same holster, so a
    price that does not move with the swatch is wrong half the time.
  */
  Quiz.prototype.buyBlock = function (product, cfg) {
    var self = this, s = this.state;
    cfg = cfg || {};
    /* The Quick View opens on whatever the card is showing, not on the
       defaults, so it never disagrees with the card behind it. */
    var sel = cfg.initial ? cfg.initial.slice() : this.defaultSelection(product);
    var box = el("div", "rg-hq__buy");
    var pills = [];

    /*
      THE OPTIONS FOLD AWAY UNTIL SOMEBODY WANTS THEM. Pablo 2026-09-30, on
      aesthetics and on CRO, and the two agree here.

      Four result cards each showing two rows of pill buttons is eight rows of
      controls on one screen, and every visible control reads as a decision the
      shopper has to make before they can buy. They do not: the quiz already
      answered both. What replaces them is a SUMMARY LINE, "Black · Right
      Hand", which is a statement rather than a question and which opens the
      pickers on a tap.

      THE SELECTION STAYS ON SCREEN, and that is not a compromise. B20 shipped
      the sticky bar's selection summary precisely because a wrong-hand holster
      is a return; hiding the hand outright would trade a return for a tidier
      card. A summary keeps the proof and drops the decision, which is the only
      version of this worth having.

      AN UNANSWERED OPTION OPENS THE PICKERS INSTEAD, because folding it away
      would hide the one thing standing between the shopper and the button.
      The magazine carrier's Model is that case.
    */
    var opts = el("div", "rg-hq__opts");
    var shown = [];
    var spec = el("button", "rg-hq__spec");
    spec.type = "button";
    var specText = el("span", "rg-hq__spec-text");
    spec.appendChild(specText);
    spec.appendChild(el("span", "rg-hq__spec-edit", COPY.specEdit));
    var open = false;
    function setOpen(next) {
      open = next;
      spec.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) opts.removeAttribute("hidden");
      else opts.setAttribute("hidden", "hidden");
    }
    spec.addEventListener("click", function () { setOpen(!open); });

    optionsOf(product).forEach(function (opt, idx) {
      var values = opt.values || [];
      if (values.length < 2) return;

      /*
        AN OPTION WE ANSWERED IS NOT A QUESTION. Pablo 2026-09-30.

        The Claw Kit comes as Standard or For Weapon Mounted Light Only, with
        no colour and nothing else to decide. The quiz asked about the light
        three screens ago, so a Style picker here is asking the shopper to
        confirm something we already know, on an add-on whose whole value is
        being one tap. The row is not rendered; the variant is still chosen,
        still priced and still added.

        NOTE WHAT IS NOT HIDDEN, because the line matters. The HAND is
        derived too and stays on screen: a wrong-hand holster is a return
        every time, and showing the selection at the point of purchase is the
        lesson B20 shipped. The magazine MODEL stays visible for the same
        reason, and because its tags may have drifted, so a shopper seeing
        the wrong size is the last line of defence.

        Only hidden when the light question was actually answered. With no
        answer the default is a guess and a guess deserves a control.
      */
      var wmlValue = first(values, function (v) {
        return v.toLowerCase().indexOf("weapon mounted light") >= 0;
      });
      if (wmlValue && s.light && sel[idx] != null) return;

      var row = el("div", "rg-hq__optrow");
      row.appendChild(el("span", "rg-hq__optname", opt.name));

      /* A short list is pills, because seeing both colours beats opening a
         menu to discover there are two. A long one is a select: six Model
         values as pills wrap to three rows on a phone and bury the button. */
      if (values.length > 3) {
        var wrapSel = el("span", "rg-hq__optselect");
        var select = el("select");
        select.setAttribute("aria-label", opt.name);
        if (sel[idx] == null) {
          var ph = el("option", null, COPY.choosePrefix + opt.name);
          ph.value = "";
          select.appendChild(ph);
        }
        values.forEach(function (v) {
          var o = el("option", null, v);
          o.value = v;
          if (v === sel[idx]) o.selected = true;
          select.appendChild(o);
        });
        select.addEventListener("change", function () {
          sel[idx] = select.value || null;
          sync();
        });
        wrapSel.appendChild(select);
        row.appendChild(wrapSel);
      } else {
        var group = el("span", "rg-hq__pills");
        values.forEach(function (v) {
          var b = el("button", "rg-hq__pill", v);
          b.type = "button";
          b.addEventListener("click", function () {
            sel[idx] = v;
            /* Remember the colour so the accessories follow the holster
               instead of defaulting to black beside a carbon fiber rig. */
            if (optionRole(opt.name) === "color") self.colorPref = v;
            sync();
          });
          group.appendChild(b);
          pills.push({ idx: idx, value: v, node: b });
        });
        row.appendChild(group);
      }
      opts.appendChild(row);
      shown.push(idx);
    });

    /* No visible option means no summary either: the comfort pad and the
       tuckable clips have one variant, and a line reading nothing but
       "Change" is worse than no line. */
    if (shown.length) {
      box.appendChild(spec);
      box.appendChild(opts);
    }

    var priceNode = cfg.priceNode || el("p", "rg-hq__price");
    if (!cfg.priceNode) box.appendChild(priceNode);

    var btn = el("button", "rg-hq__cta rg-hq__add", COPY.addCta);
    btn.type = "button";
    box.appendChild(btn);

    var busy = false;
    function sync() {
      pills.forEach(function (pill) {
        var on = sel[pill.idx] === pill.value;
        pill.node.className = "rg-hq__pill" + (on ? " is-selected" : "") +
          (self.valueReachable(product, sel, pill.idx, pill.value) ? "" : " is-gone");
        pill.node.setAttribute("aria-pressed", on ? "true" : "false");
      });

      var v = self.variantFor(product, sel);
      /* Remembered per product, so the Quick View opens on this selection. */
      if (cfg.handle) {
        self.sels = self.sels || {};
        self.sels[cfg.handle] = sel.slice();
      }
      /* The card's image follows the selection; see fill. */
      if (cfg.onVariant) {
        try { cfg.onVariant(v, sel); } catch (e) { /* never block the buy block */ }
      }
      priceNode.innerHTML = "";
      if (v) {
        priceNode.appendChild(el("span", "rg-hq__price-now", money(v.price / 100)));
        if (typeof v.compare_at_price === "number" && v.compare_at_price > v.price) {
          priceNode.appendChild(el("s", "rg-hq__price-was", money(v.compare_at_price / 100)));
        }
      }

      var missing = null;
      optionsOf(product).forEach(function (opt, i) {
        if (sel[i] == null && !missing) missing = opt.name;
      });

      /* The summary says what is going in the cart, in the order the product
         lists its options, and only for the ones the shopper can see. An
         option we answered and hid is not summarised either: the Claw Kit's
         row of text already says which version it is. */
      specText.textContent = missing
        ? COPY.choosePrefix + missing
        : shown.map(function (i) { return sel[i]; }).join(" · ");

      if (busy) return;
      if (missing) {
        btn.textContent = COPY.choosePrefix + missing;
        btn.disabled = true;
      } else if (!v || !v.available) {
        btn.textContent = COPY.soldOut;
        btn.disabled = true;
      } else {
        btn.textContent = COPY.addCta;
        btn.disabled = false;
      }
    }

    btn.addEventListener("click", function () {
      var v = self.variantFor(product, sel);
      /*
        AVAILABILITY IS RE-CHECKED HERE, not just in sync(). sync() disables
        the button for a sold-out variant, so in a browser this is unreachable
        and looks redundant. It is not: `disabled` is a DOM state, this is a
        cart write, and the two should not be one keystroke apart. A test that
        drove the button directly walked straight past the disabled attribute
        and would have posted a sold-out variant.
      */
      if (!v || !v.available || busy) return;
      busy = true;
      /* The label is REPLACED, never hidden. See B24: the theme's own button
         animated its label to opacity 0 on tap and only restored it when the
         network came back, which on a phone is a blank button for over a
         second on the one control that matters. */
      btn.textContent = COPY.addBusy;
      btn.disabled = true;
      self.addToCart([{ id: v.id, quantity: 1 }], function () {
        busy = false;
        btn.textContent = COPY.addDone;
        btn.classList.add("is-added");
        track("holster_quiz_add_to_cart", {
          hf_make: s.make, hf_model: s.model, hq_hand: s.hand,
          hq_style: cfg.style || null, hq_variant: v.id, hq_price: v.price / 100
        });
        if (cfg.onAdded) cfg.onAdded();
        /* Back to a live button, because the second accessory is a second
           decision and a permanent "Added" reads as a dead control. */
        setTimeout(function () {
          btn.classList.remove("is-added");
          sync();
        }, 2200);
      }, function () {
        busy = false;
        btn.textContent = COPY.addFail;
        btn.disabled = false;
        btn.classList.add("is-error");
      });
    });

    /* Folded when every option is answered, open when one is not. The
       shopper never has to discover a control to get past a question. */
    setOpen(sel.some(function (v, i) { return v == null && shown.indexOf(i) >= 0; }));
    sync();
    /* How a change made in the Quick View reaches the card behind it. */
    if (cfg.handle) {
      box.setSel = function (next) {
        next.forEach(function (v, i) { sel[i] = v; });
        sync();
      };
      self.buys = self.buys || {};
      self.buys[cfg.handle] = box;
    }
    return box;
  };

  /*
    THE CART BAR. It reports the REAL cart, read back from /cart.js after
    every add, rather than a count this file keeps for itself. Anything
    already in the cart from another visit is in it too, which is correct: it
    is what they will be charged for.

    Fixed to the bottom on purpose. The results are a scrolling list and the
    moment someone adds a second item is the moment the first scrolls away.

    BACK ALONGSIDE THE DRAWER, 2026-10-31. It was limited to "Rebuy missing"
    on 2026-10-17 because the two read as competing carts. Pablo restored it:
    the drawer still opens on every add, and this is what is left on screen
    once the drawer closes. Its checkout goes straight to /checkout, which
    skips Redo's offer and the drawer's upsells, so Redo's take rate is one to
    watch. bindCartSync() keeps it right when the drawer changes the cart.
  */
  Quiz.prototype.syncCartBar = function () {
    var self = this;
    var on = this.state.step === "results" && this.cart &&
             typeof this.cart.item_count === "number" && this.cart.item_count > 0;
    if (!on) {
      if (this.bar && this.bar.parentNode) this.bar.parentNode.removeChild(this.bar);
      this.bar = null;
      if (this.root && this.root.classList) this.root.classList.remove("rg-hq--has-cart");
      return;
    }
    if (!this.bar) {
      this.bar = el("div", "rg-hq__cartbar");
      this.bar.appendChild(el("span", "rg-hq__cartcount"));
      var go = el("a", "rg-hq__cta rg-hq__checkout", COPY.cartCheckout);
      go.href = "/checkout";
      go.addEventListener("click", function () {
        track("holster_quiz_checkout", {
          hq_from: "bar",
          hq_items: self.cart ? self.cart.item_count : null,
          hq_total: self.cart ? self.cart.total_price / 100 : null
        });
      });
      this.bar.appendChild(go);
      this.root.appendChild(this.bar);
    }
    this.root.classList.add("rg-hq--has-cart");
    var n = this.cart.item_count;
    this.bar.querySelector(".rg-hq__cartcount").textContent =
      n + (n === 1 ? COPY.cartItem : COPY.cartItems) +
      " · " + money(this.cart.total_price / 100);
  };

  /*
    REMOVALS AND UPSELLS HAPPEN IN THE DRAWER, where this file cannot see them,
    so the strip re-reads the cart whenever Rebuy reports a change. Debounced
    because one drawer action can fire the event more than once, and this page
    already re-reads the cart plenty (B23b).
  */
  Quiz.prototype.bindCartSync = function () {
    if (this.cartSyncBound || !document.addEventListener) return;
    this.cartSyncBound = true;
    var self = this, timer = null;
    document.addEventListener("rebuy:cart.change", function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { timer = null; self.refreshCart(); }, 300);
    });
  };

  Quiz.prototype.card = function (p, position, isTop) {
    var self = this, s = this.state;
    var card = el("div", "rg-hq__card" + (isTop ? " rg-hq__card--top" : ""));
    card.setAttribute("data-handle", p.handle);

    /*
      THE TITLE, IMAGE AND REVIEW COUNT OPEN THE QUICK VIEW. Pablo 2026-10-31,
      replacing the new-tab product links of 2026-10-28. Those answered the
      right finding, Clarity's top dead clicks were card titles and images, but
      sent the shopper off the page to get an answer it can give here. The
      product page is still one tap away, at the bottom of the sheet.
    */
    function openQuickView(from) {
      self.quickView(p.handle, {
        title: titleBtn.textContent, ra: p.ra, rc: p.rc, style: p.style, feats: p.feats
      }, from);
    }
    function qvButton(cls, text) {
      var b = el("button", cls, text);
      b.type = "button";
      b.setAttribute("data-qv", "");
      b.addEventListener("click", function () { openQuickView(b); });
      return b;
    }

    /* A div, because it now holds the photo pager's buttons. A tap on a photo
       opens the sheet; the pager's own buttons do not. */
    var media = el("div", "rg-hq__media");
    media.addEventListener("click", function (e) {
      if (e && e.target && e.target.closest && e.target.closest(".rg-hq__pager")) return;
      openQuickView(titleBtn);
    });
    card.appendChild(media);

    var body = el("div", "rg-hq__body");
    if (isTop) body.appendChild(el("p", "rg-hq__eyebrow", COPY.matchH));
    /*
      THE REAL PRODUCT TITLE, filled in by fill() when the fetch lands. Pablo
      2026-09-28: the card used to show the style name, so four results could
      read "IWB KYDEX Holster" and "Druid KYDEX Holster" while the pages they
      linked to were titled for the shopper's actual gun.

      The style name is the placeholder until then, rather than an empty box,
      so the card is readable from the first paint and the swap only adds the
      gun. The title is NOT in the build-time table on purpose: that would be
      600-odd more strings in every page for something the fetch already
      carries, and unlike a price a title changing under the shopper is
      harmless.
    */
    var title = el("h3", "rg-hq__style");
    var titleBtn = qvButton("rg-hq__plink", p.style);
    titleBtn.setAttribute("data-title", "");
    title.appendChild(titleBtn);
    body.appendChild(title);

    /*
      HOW IT CARRIES, named rather than implied. Pablo 2026-09-30: a shopper
      does not necessarily know that a paddle holster is worn outside the
      waistband, and the product titles do not all say so. The Druid carries
      both ways and gets both flags, which is the case that makes a flag worth
      having at all.

      Read from the same two pools the ranking uses, so a flag can never
      disagree with the filtering that put the card on the page.
    */
    var ways = [];
    if (IWB.indexOf(p.style) >= 0) ways.push("IWB");
    if (OWB.indexOf(p.style) >= 0) ways.push("OWB");
    if (ways.length) {
      var flags = el("p", "rg-hq__ways");
      flags.setAttribute("aria-label",
        ways.length === 2 ? "Carries inside or outside the waistband"
          : (ways[0] === "IWB" ? "Carries inside the waistband"
                               : "Carries outside the waistband"));
      ways.forEach(function (w) { flags.appendChild(el("span", "rg-hq__way", w)); });
      body.appendChild(flags);
    }

    /* The one thing that separates this style from its neighbours in the
       list. Above the price, because it is the reason to read on. */
    if (TAG[p.style]) body.appendChild(el("p", "rg-hq__tag", TAG[p.style]));

    // Reserved height, always present, so a price arriving cannot shift layout.
    var price = el("p", "rg-hq__price");
    price.setAttribute("data-price", "");
    body.appendChild(price);

    /*
      Reviews come from the BUILD-TIME table, unlike the price. There is no
      live equivalent: product.js carries no review data, Yotpo is a separate
      widget, and these are style-level aggregates that move slowly. A review
      count a few weeks stale is not the same risk as a stale price.
    */
    if (p.rc) {
      var rev = el("p", "rg-hq__reviews");
      if (p.ra) {
        rev.appendChild(el("span", "rg-hq__stars", "\u2605"));
        rev.appendChild(el("span", "rg-hq__rating", Number(p.ra).toFixed(1)));
      }
      rev.appendChild(qvButton("rg-hq__rcount", reviewCount(p.rc)));
      body.appendChild(rev);
    }

    if (isTop) {
      var clk = this.clock();
      body.appendChild(el("h4", "rg-hq__why-head", COPY.whyH));
      var why = el("ul", "rg-hq__why");
      [
        /* "Made for Your Glock G19" rather than "Molded for the Glock G19
           with nothing mounted under the barrel". The light is still named
           when there IS one, because that is what makes the fit different;
           naming its absence told the shopper something they already knew. */
        "Made for Your " + displayName(s.model) + (s.light === NO_LIGHT ? "" : " with a " + s.light),
        /* Built from the chosen position rather than a fixed list of four, so
           it stays true as the catalogue's positions change. */
        (function () {
          var side = s.carry === "o" ? "outside the waistband"
                   : s.carry === "u" ? "either way" : "inside the waistband";
          /*
            "any" is an opt-out, not a position, so it must not be read back as
            one: "Set up for not sure / show me everything carry" is what this
            line says if it is treated like the others. The side is still known
            when the shopper opted out of the position, so say that much.
          */
          var realPos = (s.pos && s.pos !== "any") ? s.pos : null;
          var sub = realPos ? self.posSub(realPos) : null;
          /* Just the position. The clock reading, the side and the draw hand
             were all chosen by the shopper two screens ago, so reciting them
             back is filler on the one line that has to justify the pick. */
          return realPos
            ? "Set up for " + POS[realPos].label.toLowerCase() + " carry"
            : s.carry === "u"
              ? "Our most popular way to carry"
              : "Worn " + side;
        })(),
        BLURB[p.style]
      ].forEach(function (t) { if (t) why.appendChild(el("li", null, t)); });
      body.appendChild(why);
      if (p.feats && p.feats.length) {
        var chips = el("div", "rg-hq__feats");
        p.feats.forEach(function (f) { chips.appendChild(el("span", "rg-hq__feat", f)); });
        body.appendChild(chips);
      }
    } else {
      body.appendChild(el("p", "rg-hq__blurb", BLURB[p.style]));
    }

    /*
      THE SLOT THE BUY BLOCK LANDS IN. Every card gets one, not just the top
      pick: Pablo 2026-09-30 asked for every item on the page to be buyable
      where it stands, and an alternate that has to be visited on a product
      page is an alternate nobody buys.

      Empty until the fetch lands, because the options and the prices are only
      in product.js. It carries a reserved height so the arrival cannot push
      the page under a thumb, which is the same rule the price node follows.
    */
    var slot = el("div", "rg-hq__buyslot");
    slot.setAttribute("data-buy", "");
    body.appendChild(slot);
    /* Compatible Guns, Features and Specs live in the sheet behind this. */
    body.appendChild(qvButton("rg-hq__details", COPY.qvOpen));
    card.appendChild(body);
    return card;
  };

  /*
    Fill in image, price and the hand-preselected link once each fetch lands.
    On failure the card keeps its Shop link and shows no price and no image,
    which is the rule: never display a price that might be wrong.
  */
  Quiz.prototype.fill = function (list) {
    var self = this;
    list.forEach(function (p) {
      var node = self.mount.querySelector('[data-handle="' + p.handle + '"]');
      if (!node) return;
      self.hydrate(p.handle, function (prod) {
        /*
          A SIZED IMAGE, AND THE TOP ONE NOT LAZY. 2026-10-17: the match card
          showed an empty box at 390px. product.js hands back the ORIGINAL,
          2000px and 336KB for the DRUID 2.0, and it was requested only after
          this fetch landed, lazily, on a phone. The same file at width=800 is
          81KB. The top card is the first thing on the results and is on
          screen when it is inserted, so it loads eagerly at high priority;
          the alternates further down stay lazy.

          THE IMAGE FOLLOWS THE COLOUR, 2026-10-28. A Glock 19 card with Black
          selected showed the Carbon Fiber Black photo, because the card only
          ever used the product's main image. setImage runs from the buy
          block's onVariant, so the first image drawn is already the selected
          variant's and a colour change swaps it. A variant with no image of
          its own keeps the product's.

          SWIPEABLE, 2026-10-31. Native scroll snap rather than Flickity: a
          phone already knows how to do this and it costs no script. The photo
          for the selected colour always leads, capped at five so a card
          cannot queue a dozen images. Only the first is ever eager, and the
          box keeps its reserved square, so none of this can shift the page.
        */
        var media = node.querySelector(".rg-hq__media");
        var top = node.classList && node.classList.contains("rg-hq__card--top");
        var fallbackImg = prod.featured_image || (prod.images && prod.images[0]);
        function setImage(src) {
          if (!src || !media) return;
          var lead = cleanSrc(src);
          if (media.getAttribute("data-lead") === lead) return;
          media.setAttribute("data-lead", lead);
          media.innerHTML = "";
          var list = uniqueSrcs([src].concat(prod.images || [])).slice(0, 5);
          var gal = el("div", "rg-hq__gal");
          gal.setAttribute("aria-hidden", "true");
          list.forEach(function (s, n) {
            var i = el("img");
            i.alt = prod.title || p.style;
            i.loading = top && n === 0 ? "eager" : "lazy";
            if (top && n === 0) i.fetchPriority = "high";
            i.width = 400; i.height = 400;
            i.sizes = top ? "(min-width: 768px) 360px, 100vw" : "(min-width: 768px) 150px, 100vw";
            i.setAttribute("data-src", s);
            i.srcset = [400, 600, 800, 1200].map(function (w) {
              return sizedImage(s, w) + " " + w + "w";
            }).join(", ");
            i.src = sizedImage(s, 800);
            gal.appendChild(i);
          });
          media.appendChild(gal);
          if (list.length > 1) addPager(media, gal, list.length);
        }
        function onVariant(v) {
          var vImg = v && v.featured_image && v.featured_image.src;
          setImage(vImg || fallbackImg);
        }

        /*
          The buy block owns the price now, because the price follows the
          colour. Handing it the card's existing price node keeps the price
          where the design put it, above the reviews, while the swatches and
          the button sit below.
        */
        var slot = node.querySelector("[data-buy]");
        if (slot && !slot.firstChild) {
          slot.appendChild(self.buyBlock(prod, {
            priceNode: node.querySelector("[data-price]"),
            style: p.style,
            handle: p.handle,
            onVariant: onVariant
          }));
        }
        /* No buy block drawn (it was already there), still show an image. */
        if (media && !media.querySelector("img")) setImage(fallbackImg);

        var titleEl = node.querySelector("[data-title]");
        if (titleEl && prod.title) titleEl.textContent = prod.title;
      }, function () {
        node.setAttribute("data-rg-hq-nodata", "true");
      });

      /* Fetched now so the sheet opens with its accordions ready. */
      self.details(p.handle);
    });
  };

  /*
    THE SHEET'S METAFIELDS, fetched once per product through
    templates/product.rg-hq-fit.liquid (?view=) and cached for the page.
    product.js carries no metafields, and the build-time catalogue keeps only
    the cheapest product per gun and light, so neither can supply them. A
    product missing any of them simply has fewer accordions.
  */
  Quiz.prototype.details = function (handle, ok, err) {
    this.fitCache = this.fitCache || {};
    if (this.fitCache[handle]) { if (ok) ok(this.fitCache[handle]); return; }
    var self = this;
    /*
      NO Accept: application/json HEADER. Shopify answers a product URL that
      asks for JSON with its own product JSON and ignores ?view= entirely, so
      the header that every other fetch here sends silently returned the wrong
      document and every accordion was removed. Found on staging 2026-10-28.
      The view's body is JSON regardless of the content type it is served as.
    */
    fetch(PRODUCT_BASE + handle + "?view=rg-hq-fit")
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error("http")); })
      .then(function (j) {
        if (!j || typeof j !== "object") return Promise.reject(new Error("shape"));
        self.fitCache[handle] = j;
        if (ok) ok(j);
      })
      .catch(function () { if (err) err(); });
  };

  /* Each entry is "<gun> | <light>". The light always shows, "No Light/Laser"
     included: in a list that mixes both, an unmarked line is a question. */
  function fitLine(entry) {
    var parts = String(entry).split("|");
    var gun = (parts[0] || "").trim();
    var light = (parts[1] || "").trim();
    return light ? gun + " (" + light + ")" : gun;
  }

  /* A metafield arrives as a list or a single value, or not at all. */
  function asList(v) {
    if (v == null || v === "") return [];
    return (Array.isArray(v) ? v : [v]).map(String).filter(Boolean);
  }

  /* KYDEX thickness, which no metafield holds. Pablo 2026-10-31: .08" on
     everything except the Druid 2.0, which is .093". */
  function materialLine(material, title) {
    if (!/^kydex$/i.test(material)) return material;
    return (/druid\s*2\.0/i.test(title || "") ? '.093"' : '.08"') + " " + material;
  }

  function cleanSrc(src) {
    return String(src || "").replace(/^https?:/, "").split("?")[0];
  }

  function uniqueSrcs(list) {
    var seen = {};
    return list.filter(function (s) {
      var k = cleanSrc(s);
      if (!k || seen[k]) return false;
      seen[k] = true;
      return true;
    });
  }

  /*
    Tells a scroll-snap strip's owner which slide is showing, and moves it.

    THE LAST PHOTO LEADS BACK TO THE FIRST. Pablo 2026-10-31. Scroll snap has
    no loop, so a copy of the first slide sits after the last, and once the
    swipe has settled on it the strip jumps to the real first slide, which
    looks identical. The jump waits for the scroll to settle because iOS drops
    a position set during momentum.
  */
  function slider(strip, onChange) {
    var current = 0, timer = null;
    var count = strip.children.length;
    if (count > 1) {
      var copy = strip.children[0].cloneNode(true);
      copy.setAttribute("aria-hidden", "true");
      strip.appendChild(copy);
    }
    function settle() {
      var w = strip.clientWidth;
      if (count > 1 && w && Math.round(strip.scrollLeft / w) >= count) strip.scrollLeft = 0;
    }
    strip.addEventListener("scroll", function () {
      var w = strip.clientWidth;
      var n = w ? Math.round(strip.scrollLeft / w) : 0;
      if (n >= count) n = 0;
      if (n !== current) { current = n; onChange(n); }
      if (timer) clearTimeout(timer);
      timer = setTimeout(settle, 100);
    }, { passive: true });
    return {
      go: function (n) { strip.scrollLeft = ((n + count) % count) * strip.clientWidth; },
      at: function () { return current; }
    };
  }

  /* Dots for a phone, arrows too where there is a mouse and no way to swipe. */
  function addPager(box, strip, count) {
    var pager = el("div", "rg-hq__pager");
    var dots = [];
    var s = slider(strip, mark);
    function mark(n) {
      dots.forEach(function (d, i) { d.className = "rg-hq__dot" + (i === n ? " is-current" : ""); });
    }
    function arrow(cls, label, step) {
      var b = el("button", "rg-hq__arrow " + cls);
      b.type = "button";
      b.setAttribute("aria-label", label);
      b.addEventListener("click", function () { s.go(s.at() + step); });
      pager.appendChild(b);
    }
    arrow("rg-hq__arrow--prev", "Previous Photo", -1);
    for (var i = 0; i < count; i++) (function (n) {
      var d = el("button", "rg-hq__dot");
      d.type = "button";
      d.setAttribute("aria-label", "Photo " + (n + 1));
      d.addEventListener("click", function () { s.go(n); });
      dots.push(d);
      pager.appendChild(d);
    })(i);
    arrow("rg-hq__arrow--next", "Next Photo", 1);
    mark(0);
    box.appendChild(pager);
  }

  /* Everything the sheet can show, photos and video, selected colour first. */
  function mediaItems(prod, leadSrc) {
    var items = [], seen = {};
    function addImage(src) {
      var k = cleanSrc(src);
      if (!k || seen[k]) return;
      seen[k] = true;
      items.push({ type: "image", src: src, thumb: src });
    }
    if (leadSrc) addImage(leadSrc);
    if (Array.isArray(prod.media) && prod.media.length) {
      prod.media.forEach(function (m) {
        var poster = (m.preview_image && m.preview_image.src) || "";
        if (m.media_type === "image") addImage(m.src);
        else if (m.media_type === "video" && m.sources && m.sources.length) {
          var mp4 = first(m.sources, function (x) { return x.format === "mp4"; }) || m.sources[0];
          items.push({ type: "video", src: mp4.url, thumb: poster });
        } else if (m.media_type === "external_video" && m.external_id) {
          items.push({ type: "external", host: m.host, id: m.external_id, thumb: poster });
        }
      });
    } else {
      (prod.images || []).forEach(addImage);
    }
    return items;
  }

  function mediaSlide(item, title, eager) {
    var slide = el("div", "rg-hq__qv-slide");
    if (item.type === "video") {
      var v = el("video");
      v.controls = true;
      v.preload = "none";
      v.setAttribute("playsinline", "");
      if (item.thumb) v.poster = sizedImage(item.thumb, 800);
      v.src = item.src;
      slide.appendChild(v);
      return slide;
    }
    if (item.type === "external") {
      var f = el("iframe");
      f.title = title;
      f.loading = "lazy";
      f.allow = "encrypted-media; picture-in-picture";
      f.allowFullscreen = true;
      f.src = item.host === "vimeo"
        ? "https://player.vimeo.com/video/" + item.id
        : "https://www.youtube-nocookie.com/embed/" + item.id;
      slide.appendChild(f);
      return slide;
    }
    var i = el("img");
    i.alt = title;
    i.loading = eager ? "eager" : "lazy";
    i.width = 800; i.height = 800;
    i.sizes = "(min-width: 768px) 560px, 100vw";
    i.srcset = [400, 800, 1200].map(function (w) {
      return sizedImage(item.src, w) + " " + w + "w";
    }).join(", ");
    i.src = sizedImage(item.src, 800);
    /* Tap to zoom, at the point tapped. Tap again to come back out. */
    slide.addEventListener("click", function (e) {
      var on = !slide.classList.contains("is-zoomed");
      if (on && e && i.getBoundingClientRect) {
        var r = i.getBoundingClientRect();
        i.style.transformOrigin = (e.clientX - r.left) + "px " + (e.clientY - r.top) + "px";
      }
      slide.classList.toggle("is-zoomed", on);
    });
    slide.appendChild(i);
    return slide;
  }

  function accordion(head, content) {
    var d = el("details", "rg-hq__fits");
    d.appendChild(el("summary", "rg-hq__fits-head", head));
    d.appendChild(content);
    return d;
  }

  function bullets(list) {
    var ul = el("ul", "rg-hq__fits-list");
    list.forEach(function (t) { ul.appendChild(el("li", null, t)); });
    return ul;
  }

  /*
    THE QUICK VIEW. Pablo 2026-10-31: everything a shopper would leave the page
    to find out, without leaving the page, and without burying the button.
    Selection, price and Add To Cart come first; the rest sits in accordions
    that start closed, so it is there for whoever wants it and costs nobody
    else a scroll.

    A bottom sheet on a phone, a centered modal on a desktop. It lives inside
    the quiz root, not on body, because the colour tokens are scoped there.

    Back closes it: on a phone that is what Back is expected to do here, and
    without the history entry it would leave the results entirely.

    The drawer still opens on an add, through Rebuy's own patch of fetch, so
    the sheet only has to get out of its way.
  */
  Quiz.prototype.quickView = function (handle, info, opener) {
    if (this.qv) return;
    var self = this, s = this.state;
    info = info || {};
    var title = info.title || "";
    var scrollY = window.pageYOffset || 0;
    var closed = false, pushed = false;

    var wrap = el("div", "rg-hq__qv");
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.setAttribute("aria-label", title);
    var back = el("div", "rg-hq__qv-back");
    var panel = el("div", "rg-hq__qv-panel");
    panel.appendChild(el("div", "rg-hq__qv-grab"));
    var x = el("button", "rg-hq__qv-close");
    x.type = "button";
    x.setAttribute("aria-label", COPY.qvClose);
    panel.appendChild(x);
    var scroll = el("div", "rg-hq__qv-scroll");
    panel.appendChild(scroll);
    wrap.appendChild(back);
    wrap.appendChild(panel);

    var html = document.documentElement, body = document.body;
    function setLock(on) {
      if (html && html.classList) html.classList.toggle("rg-hq-qv-lock", on);
      if (body && body.classList) body.classList.toggle("rg-hq-qv-lock", on);
      /* position: fixed is the only lock iOS honours, and it needs the offset
         or the page jumps to the top behind the sheet. */
      if (body && body.style) body.style.top = on ? (-scrollY) + "px" : "";
    }

    function close() {
      if (closed) return;
      closed = true;
      self.qv = null;
      if (document.removeEventListener) document.removeEventListener("keydown", onKey);
      if (window.removeEventListener) window.removeEventListener("popstate", onPop);
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      self.root.classList.remove("rg-hq--qv");
      setLock(false);
      if (window.scrollTo) window.scrollTo(0, scrollY);
      if (pushed && window.history && history.state && history.state.rgHqQv) history.back();
      if (opener && opener.focus) {
        try { opener.focus({ preventScroll: true }); } catch (e) { /* focus is a courtesy */ }
      }
    }
    function onPop() { pushed = false; close(); }
    function onKey(e) {
      if (e.key === "Escape") { close(); return; }
      if (e.key !== "Tab") return;
      var f = panel.querySelectorAll("a[href], button:not([disabled]), select, summary, video, iframe");
      if (!f.length) return;
      var a = document.activeElement;
      if (e.shiftKey && (a === f[0] || !panel.contains(a))) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && a === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
    x.addEventListener("click", close);
    back.addEventListener("click", close);

    /* Swipe down from the top of the sheet closes it, as a sheet should. */
    var y0 = null, x0 = 0;
    panel.addEventListener("touchstart", function (e) {
      y0 = scroll.scrollTop > 0 ? null : e.touches[0].clientY;
      x0 = e.touches[0].clientX;
    }, { passive: true });
    panel.addEventListener("touchend", function (e) {
      if (y0 === null) return;
      var t = e.changedTouches[0], dy = t.clientY - y0, dx = t.clientX - x0;
      if (dy > 90 && Math.abs(dy) > Math.abs(dx)) close();
      y0 = null;
    }, { passive: true });

    var heading = el("h3", "rg-hq__qv-title", title);
    var slot = el("div", "rg-hq__qv-main");
    var more = el("div", "rg-hq__qv-more");
    var full = el("a", "rg-hq__qv-full", COPY.qvFull);
    full.href = PRODUCT_BASE + handle;
    var gallery = el("div", "rg-hq__qv-media");
    scroll.appendChild(gallery);
    scroll.appendChild(heading);
    scroll.appendChild(slot);
    scroll.appendChild(more);
    scroll.appendChild(full);

    self.qv = wrap;
    self.qvCount = (self.qvCount || 0) + 1;
    self.root.appendChild(wrap);
    self.root.classList.add("rg-hq--qv");
    setLock(true);
    document.addEventListener("keydown", onKey);
    if (window.history && history.pushState) {
      try {
        history.pushState({ rgHqQv: true }, "");
        pushed = true;
        window.addEventListener("popstate", onPop);
      } catch (e) { /* the X and the backdrop still close it */ }
    }
    if (x.focus) x.focus({ preventScroll: true });

    /* An impression count, not view_item: the sheet is not a product page, and
       a GTM tag keyed on view_item would count it as one. The counter stops
       track() deduping a second look at the same product. */
    track("holster_quiz_quick_view", {
      hf_make: s.make, hf_model: s.model, hq_style: info.style || null,
      hq_handle: handle, hq_open: self.qvCount
    });

    this.hydrate(handle, function (prod) {
      if (closed) return;
      if (prod.title) {
        heading.textContent = prod.title;
        wrap.setAttribute("aria-label", prod.title);
      }
      var initial = self.sels && self.sels[handle];
      var lead = initial && self.variantFor(prod, initial);
      var leadSrc = lead && lead.featured_image && lead.featured_image.src;
      /*
        CAPPED AT TWELVE, led by the selected variant's photo. The Magazine
        Holster has 53, one set per gun, and nobody swipes through a catalogue.
        A selection whose photo fell past the cap redraws with it in front.
      */
      var items, slides;
      function drawGallery(src) {
        gallery.innerHTML = "";
        items = mediaItems(prod, src).slice(0, 12);
        var strip = el("div", "rg-hq__qv-gal");
        var thumbs = el("div", "rg-hq__qv-thumbs");
        var thumbNodes = [];
        items.forEach(function (it, n) {
          strip.appendChild(mediaSlide(it, prod.title || title, n === 0));
        });
        gallery.appendChild(strip);
        function mark(n) {
          thumbNodes.forEach(function (t, i) {
            t.className = "rg-hq__qv-thumb" + (t.getAttribute("data-video") ? " is-video" : "") +
              (i === n ? " is-current" : "");
          });
        }
        slides = slider(strip, mark);
        if (items.length > 1) {
          items.forEach(function (it, n) {
            var t = el("button", "rg-hq__qv-thumb");
            t.type = "button";
            t.setAttribute("aria-label", "Show Photo " + (n + 1));
            if (it.thumb) {
              var ti = el("img");
              ti.src = sizedImage(it.thumb, 160);
              ti.alt = "";
              ti.loading = "lazy";
              ti.width = 64; ti.height = 64;
              t.appendChild(ti);
            }
            if (it.type !== "image") t.setAttribute("data-video", "true");
            t.addEventListener("click", function () { slides.go(n); });
            thumbNodes.push(t);
            thumbs.appendChild(t);
          });
          gallery.appendChild(thumbs);
          mark(0);
        }
      }
      drawGallery(leadSrc || prod.featured_image);

      var price = el("p", "rg-hq__price rg-hq__qv-price");
      slot.appendChild(price);
      var ra = info.ra, rc = info.rc;
      if (rc) {
        var rev = el("p", "rg-hq__reviews");
        if (ra) {
          rev.appendChild(el("span", "rg-hq__stars", "★"));
          rev.appendChild(el("span", "rg-hq__rating", Number(ra).toFixed(1)));
        }
        rev.appendChild(el("span", "rg-hq__rcount-text", reviewCount(rc)));
        slot.appendChild(rev);
      }
      slot.appendChild(self.buyBlock(prod, {
        priceNode: price,
        style: info.style || title,
        initial: initial,
        onVariant: function (v, sel) {
          if (v && v.id) full.href = PRODUCT_BASE + handle + "?variant=" + v.id;
          /* Keep the card behind the sheet saying the same thing. */
          var cardBuy = self.buys && self.buys[handle];
          if (cardBuy) cardBuy.setSel(sel);
          var src = v && v.featured_image && v.featured_image.src;
          if (src) {
            var at = -1;
            items.forEach(function (it, n) {
              if (at < 0 && it.type === "image" && cleanSrc(it.src) === cleanSrc(src)) at = n;
            });
            if (at < 0) drawGallery(src);
            else if (at !== slides.at()) slides.go(at);
          }
        },
        onAdded: close
      }));
      /* Ours alone: an accessory from another brand must not claim this. */
      if (/rounded/i.test(prod.vendor || "")) {
        slot.appendChild(el("p", "rg-hq__qv-trust", COPY.qvTrust));
      }

      function drawMore(d) {
        if (closed) return;
        d = d || {};
        var guns = asList(d.fitments);
        if (guns.length) {
          more.appendChild(accordion(COPY.fitsH + " (" + guns.length + ")", bullets(guns.map(fitLine))));
        }
        var feats = asList(d.features).map(function (f) { return f.replace(/['"]/g, ""); });
        if (!feats.length) feats = asList(info.feats);
        if (feats.length) more.appendChild(accordion(COPY.qvFeatures, bullets(feats)));

        var rows = [
          ["Material", asList(d.material).map(function (m) { return materialLine(m, prod.title); })],
          ["Carry Type", asList(d.carry_type)],
          ["Carry Position", asList(d.carry_position)],
          ["Style", asList(d.style)]
        ];
        optionsOf(prod).forEach(function (o) {
          var vals = (o.values || []).filter(function (v) { return v !== "Default Title"; });
          if (vals.length) rows.push([o.name, vals]);
        });
        rows = rows.filter(function (r) { return r[1].length; });
        if (rows.length) {
          var dl = el("dl", "rg-hq__qv-specs");
          rows.forEach(function (r) {
            dl.appendChild(el("dt", null, r[0]));
            dl.appendChild(el("dd", null, r[1].join(", ")));
          });
          more.appendChild(accordion(COPY.qvSpecs, dl));
        }

        var ship = el("div", "rg-hq__qv-ship");
        /* Our cutoff, so not promised on what a partner ships. */
        if (/rounded/i.test(prod.vendor || "")) ship.appendChild(el("p", null, COPY.qvShipTime));
        ship.appendChild(el("p", null, COPY.qvShipLine));
        [[COPY.qvShipLink, "/pages/rounded-by-concealment-express-shipping-fulfillment-policy"],
         [COPY.qvReturnLink, "/pages/concealment-express-exchange-return-policy"]].forEach(function (l) {
          var a = el("a", null, l[0]);
          a.href = l[1];
          a.target = "_blank";
          a.rel = "noopener";
          ship.appendChild(a);
        });
        more.appendChild(accordion(COPY.qvShipping, ship));
      }
      self.details(handle, drawMore, function () { drawMore(null); });
    }, function () {
      /* No product data: the full page link at the bottom is the way forward. */
    });
  };

  Quiz.prototype.addons = function (wrap, top) {
    var s = this.state;
    /*
      THREE ACCESSORIES FOR EVERY WINNING STYLE. Pablo 2026-09-30.

      Written as ONE ORDERED POOL with a gate on each entry, taking the first
      three that pass, rather than as a list per style. Fourteen styles times
      three slots is a table nobody maintains, and it fails silently: a style
      added to the ranking would show nothing here and no error would say so.
      A pool degrades instead, and the last entries are deliberately gateless
      so there is always something left to fall back to.

      ORDER IS RELEVANCE. The first thing an inside-waistband shopper wants is
      the claw, then the pad, then a spare magazine. An outside-waistband
      shopper fails both of the first two and lands on the magazine, the
      mounting hardware for their own style, and the lubricant.

      THE DRUID SITS IN BOTH POOLS, which is why every gate reads the carry
      answer rather than the style name.
    */
    /*
      WORN INSIDE THE WAISTBAND, which is NOT the same question as "is in the
      IWB ranking pool", and so is not the same list.

      IWB deliberately leaves out the plain universals because they are not
      offered as gun-specific results. But a Universal IWB holster and an IWB
      leather holster are both still worn inside the waistband, and a comfort
      pad still fits them. Reusing the ranking pool here silently denied those
      two styles the pad and the claw, which showed up only when the accessory
      set was printed for all fourteen styles rather than for the three worth
      trying by hand.

      Not matched on the name containing "IWB" either: that reads as clever
      and breaks the first time a style is renamed.
    */
    var WORN_INSIDE = IWB.concat(["Universal IWB Holster", "IWB Leather Holster"]);
    var inside = WORN_INSIDE.indexOf(top.style) >= 0 && s.carry !== "o";

    /*
      WHICH HOLSTERS THE COMFORT PAD FITS. Pablo 2026-09-30, and a NARROWER
      list than "worn inside the waistband", which is what it was gated on
      before and which was wrong.

      It is a named list rather than a rule because it does not follow from
      anything the quiz already knows. Worn-inside would have offered it to
      the Sidecar, the Trigger Guard, the leather holster and the X-FER, and
      nothing in the data marks those out. Only somebody who has fitted the
      pad to the shell can say.

      The carry answer still applies on top: the Druid is on this list and is
      also worn outside the waistband, where a pad between the shell and the
      shopper is not a thing that happens.

      BOTH LUX STYLES ARE INCLUDED on the reading that "Lux" means the family.
      If the V2 EXT is different enough to be excluded, remove it here.
    */
    var PAD_STYLES = [
      "IWB KYDEX Holster",
      "IWB KYDEX Plus Line Holster",
      "Tuckable IWB KYDEX Holster",
      "Athletic Wear Tuckable IWB Holster",
      "Druid KYDEX Holster",
      "LUX Universal Light Bearing Holster",
      "LUX V2 EXT Universal Light Bearing Holster",
      "Universal IWB Holster"
    ];
    var outside = OWB.indexOf(top.style) >= 0 && !inside;
    var tuckable = top.style.indexOf("Tuckable") >= 0;

    var POOL = [
      /* Skipped when the holster already includes a claw. */
      ["Holster Claw Kit",
       "Rotates the grip into your body for Maximum Concealment",
       "holster-claw-kit",
       inside && !!top.feats && top.feats.indexOf("Claw Compatible") >= 0 &&
         CLAW_INCLUDED.indexOf(top.style) < 0 &&
         CLAW_MISFLAGGED.indexOf(top.style) < 0],

      /* Selling fast, and the accessory that most obviously belongs to an IWB
         result: it sits between the holster and the shopper. One variant, so
         one tap. No Yotpo reviews yet, so no stars, which is why it is not in
         the generator's ACCESSORY_HANDLES; that list drives the rating and its
         guard rightly refuses to build an entry with no review data. */
      ["Holster Shield Comfort Pad",
       "Sits between the holster and you for all-day comfort",
       "holster-shield-universal-iwb-comfort-pad",
       PAD_STYLES.indexOf(top.style) >= 0 && s.carry !== "o"],

      /* Not offered on the Sidecar, which carries a spare magazine already. */
      ["Magazine Holster (IWB/OWB KYDEX)",
       "Carry a spare mag alongside your holster",
       "iwb-owb-kydex-magazine-holster",
       top.style !== "Sidecar Tuckable IWB KYDEX Holster"],

      /* A tuckable holster is bought to disappear under a tucked shirt, and
         the clip is what decides whether it does. One variant. */
      ["ULTICLIP3+ Tuckable Clip",
       "Grips the waistband itself, so no clip shows over your belt",
       "ulticlip3-tuckable-holster-clip",
       tuckable],

      /* The two OWB mounts, each offered only to the style it fits. */
      ["Premium Paddle",
       "A second paddle, so the holster moves between belts without tools",
       "premium-paddle-for-owb-holsters",
       outside && top.style.indexOf("Paddle") >= 0],
      ["OWB Belt Loops",
       "Spare loops to re-set the ride height and cant",
       "owb-holster-belt-loops",
       outside && top.style.indexOf("Belt Loop") >= 0],

      /* GATELESS, ON PURPOSE. These fit any holster and any carry position, so
         they are what stops a style falling short of three. Last because they
         fill a slot rather than earning it. */
      ["Spare Hardware Kit",
       "Spare screws and washers to re-tension the holster",
       "spare-holster-hardware-kits",
       true],
      ["Quick Draw Lubricant",
       "Keeps the draw smooth and the KYDEX quiet",
       "quick-draw-holster-gun-lubricant",
       true]
    ];

    var list = [];
    POOL.forEach(function (p) {
      if (p[3] && list.length < 3) list.push([p[0], p[1], p[2]]);
    });
    if (!list.length) return;
    wrap.appendChild(el("h3", "rg-hq__alt-head", COPY.addonsH));
    var box = el("div", "rg-hq__addons");
    var self = this, D = this.data;
    list.forEach(function (a) {
      /* A DIV, not a link. These are bought here like everything else on the
         page; there is nowhere left to send anybody. The handle moves onto the
         element because it used to be readable off the href, and the rating
         check reads it. */
      var row = el("div", "rg-hq__addon");
      row.setAttribute("data-handle", a[2]);

      /*
        A THUMBNAIL AND A RATING, like the holster cards above. Pablo
        2026-09-30. Two text rows in a box were the only things on the results
        page asking to be bought without showing what they are.
      */
      var media = el("span", "rg-hq__addon-media");
      row.appendChild(media);

      var body = el("span", "rg-hq__addon-body");
      var addonName = el("button", "rg-hq__addon-name", a[0]);
      addonName.type = "button";
      var openAddon = function () {
        self.quickView(a[2], { title: a[0], ra: rev && rev[1], rc: rev && rev[0], style: a[0] }, addonName);
      };
      addonName.addEventListener("click", openAddon);
      media.addEventListener("click", openAddon);
      body.appendChild(addonName);
      body.appendChild(el("span", "rg-hq__addon-why", a[1]));

      /*
        THE RATING COMES FROM THE PAYLOAD, not from the product fetch, for the
        same reason the holster cards' does: product.js carries no review data
        at all. These two are in the build-time table under "A", pulled from
        the same export by handle. No entry means no stars rather than a guess.
      */
      var rev = D.A && D.A[a[2]];
      if (rev && rev[0]) {
        var line = el("span", "rg-hq__addon-reviews");
        if (rev[1]) {
          line.appendChild(el("span", "rg-hq__stars", "★"));
          line.appendChild(el("span", "rg-hq__rating", Number(rev[1]).toFixed(1)));
        }
        line.appendChild(el("span", "rg-hq__rcount", reviewCount(rev[0])));
        body.appendChild(line);
      }
      var slot = el("span", "rg-hq__addon-buy");
      body.appendChild(slot);
      row.appendChild(body);
      box.appendChild(row);

      /*
        THE HOLSTER'S FETCH COMES FIRST, because its tags are what size the
        magazine carrier. Both are cached, and the holster's is the one the
        top card already asked for, so this costs no extra request; it only
        orders them. Without the ordering the carrier would sometimes render
        its Model picker before the tags arrived and ask a question it did
        not need to, which is the kind of bug that reproduces one time in
        five and gets called flaky.

        On failure the accessory still renders and still sells. It just asks.
      */
      var withTags = function (go) {
        self.hydrate(top.handle, function (hp) {
          self.gunTags = (hp && hp.tags) || [];
          go();
        }, go);
      };
      withTags(function () { self.hydrate(a[2], function (prod) {
        if (!slot.firstChild) slot.appendChild(self.buyBlock(prod, { style: a[0], handle: a[2] }));
        /*
          The Style picker is hidden on the Claw Kit because the light answer
          decides it, so the row says in words which one is going in the cart.
          Not a control, just the sentence that stops a silent wrong variant
          being the price of one-tap.
        */
        var pickedWml = (prod.variants || []).some(function (v) {
          return (v.options || []).some(function (o) {
            return String(o).toLowerCase().indexOf("weapon mounted light") >= 0;
          });
        }) && s.light && s.light !== NO_LIGHT;
        if (pickedWml) body.appendChild(el("span", "rg-hq__addon-why", COPY.addonWml));
        var img = prod.featured_image || (prod.images && prod.images[0]);
        if (!img || media.firstChild) return;
        var i = el("img");
        i.src = img.indexOf("//") === 0 ? "https:" + img : img;
        i.alt = prod.title || a[0];
        i.loading = "lazy";
        i.width = 120; i.height = 120;
        media.appendChild(i);
      }, function () { /* no image and no buy block; the row still reads. */ }); });
    });
    wrap.appendChild(box);
  };

  /* buildDisplay and displayName are exported for the test harness, which
     builds a Quiz without running the constructor and would otherwise never
     exercise the shortening at all. */
  window.RGHolsterQuiz = { build: BUILD, Quiz: Quiz, track: track, makeOf: makeOf, BLURB: BLURB,
                           IWB: IWB, OWB: OWB, CLAW_INCLUDED: CLAW_INCLUDED, CLAW_MISFLAGGED: CLAW_MISFLAGGED,
                           buildDisplay: buildDisplay, displayName: displayName, materialLine: materialLine };

  function init() {
    var roots = document.querySelectorAll("[data-rg-hq-root]");
    for (var i = 0; i < roots.length; i++) new Quiz(roots[i]);
    /* iOS Safari applies :active only when some touch listener exists, so
       without this the answers' pressed state never shows on an iPhone. */
    if (roots.length) {
      try { document.addEventListener("touchstart", function () {}, { passive: true }); } catch (e) {}
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
